/**
 * Counterpunch App - Single Page Application Navigation
 *
 * This module handles:
 * - Intercepting internal link clicks
 * - Fetching and loading page content dynamically
 * - Updating browser history and URL
 * - Browser back/forward button support
 * - Page caching
 */

import {
  init as plausibleInit,
  track as plausibleTrack,
} from "@plausible-analytics/tracker";
import { marked } from "marked";
import { settings } from "./settings.js";
import { getPricingProducts } from "./pricing-config.js";

// Page titles for SPA navigation
const PAGE_TITLES = {
  "/": "Counterpunch - Modern Font Editor",
  "/about": "About Counterpunch",
  "/pricing": "Pricing - Counterpunch",
  "/articles": "Articles - Counterpunch",
  "/documentation": "Documentation - Counterpunch",
  "/login": "Login - Counterpunch",
  "/invite": "Invitation - Counterpunch",
  "/transfer": "Ownership Transfer - Counterpunch",
  "/account": "Account - Counterpunch",
  "/admin/dashboard": "Admin Dashboard - Counterpunch",
  "/admin/errors": "Error Details - Counterpunch",
  "/privacy": "Privacy Policy - Counterpunch",
  "/terms": "Terms of Service - Counterpunch",
};

// Helper function to get model display name
// Maps model IDs to their short names (must stay in sync with utils/ai-settings.js)
function getModelDisplayName(modelId) {
  const modelNames = {
    "claude-sonnet-4-6": "Sonnet",
    "kimi-2.6": "Kimi",
    "deepseek-v4-flash": "DeepSeek",
  };
  return modelNames[modelId] || modelId;
}

// EU VAT rates (as of 2026) - Standard rates
const EU_VAT_RATES = {
  AUT: 0.2, // Austria
  BEL: 0.21, // Belgium
  BGR: 0.2, // Bulgaria
  HRV: 0.25, // Croatia
  CYP: 0.19, // Cyprus
  CZE: 0.21, // Czech Republic
  DNK: 0.25, // Denmark
  EST: 0.22, // Estonia
  FIN: 0.255, // Finland
  FRA: 0.2, // France
  DEU: 0.19, // Germany
  GRC: 0.24, // Greece
  HUN: 0.27, // Hungary
  IRL: 0.23, // Ireland
  ITA: 0.22, // Italy
  LVA: 0.21, // Latvia
  LTU: 0.21, // Lithuania
  LUX: 0.17, // Luxembourg
  MLT: 0.18, // Malta
  NLD: 0.21, // Netherlands
  POL: 0.23, // Poland
  PRT: 0.23, // Portugal
  ROU: 0.19, // Romania
  SVK: 0.2, // Slovakia
  SVN: 0.22, // Slovenia
  ESP: 0.21, // Spain
  SWE: 0.25, // Sweden
};

class AppNavigator {
  constructor() {
    this.pageCache = new Map();
    this.contentContainer = null;
    this.currentPath = window.location.pathname;
    this.trackingParams = {}; // Store tracking parameters
    this.currentUser = null; // Store current user session
    this.isAdminUser = false;
    this.accountMembership = null;
    this.adminMembershipLookupResult = null;
    this.adminCloudLookupResult = null;

    // List of common tracking/marketing parameters to remove from URL
    this.trackingParamNames = [
      // UTM parameters
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      // Facebook
      "fbclid",
      "fb_action_ids",
      "fb_action_types",
      "fb_source",
      "fb_ref",
      // Google
      "gclid",
      "gclsrc",
      "dclid",
      // Microsoft/Bing
      "msclkid",
      // Twitter
      "twclid",
      // LinkedIn
      "li_fat_id",
      // TikTok
      "ttclid",
      // Other common parameters
      "mc_cid",
      "mc_eid",
      "_ga",
      "ref",
      "referrer",
    ];

    // Initialize Plausible Analytics with proxy endpoint
    plausibleInit({
      domain: "counterpunch.space", // Always use production domain for unified analytics
      captureOnLocalhost: true, // Allow tracking on localhost for development
      autoCapturePageviews: false, // We'll manually track pageviews
      endpoint: window.location.origin + "/api/event", // Use our own domain as proxy
    });
    console.log(
      "✅ Plausible initialized with endpoint:",
      window.location.origin + "/api/event",
    );
    console.log("📊 Tracking domain:", "counterpunch.space");

    // Capture tracking params immediately, even before DOM is ready
    this.captureAndCleanTrackingParams();

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    this.contentContainer = document.querySelector(".main-content");

    if (!this.contentContainer) {
      console.error("Main content container not found");
      return;
    }

    // Keep handbook screenshots in sync when the theme attribute changes
    new MutationObserver(() => this.refreshDocumentationScreenshots()).observe(
      document.documentElement,
      { attributes: true, attributeFilter: ["data-theme"] },
    );

    // Check for URL parameters
    const params = new URLSearchParams(window.location.search);

    // Check for login success
    if (params.get("login") === "success") {
      // Clear the parameter from URL
      window.history.replaceState({}, "", window.location.pathname);
      console.log("✅ Login successful");
    }

    // Check for error messages and display them
    if (params.get("error")) {
      this.showAuthError(params.get("error"));
      // Clear the error from URL
      window.history.replaceState({}, "", window.location.pathname);
    }

    await this.refreshCurrentUser();

    // Initialize auth UI
    this.updateAuthUI();

    // Intercept all clicks on links
    document.addEventListener("click", (e) => this.handleLinkClick(e));

    // Handle browser back/forward buttons
    window.addEventListener("popstate", (e) => this.handlePopState(e));

    // Load initial page based on current URL
    const currentPath = window.location.pathname;
    console.log("📍 Initial page load, currentPath:", currentPath);

    // Set up footer width syncing (do this for all pages)
    // Sync footer section widths with header (with delay for layout to settle)
    requestAnimationFrame(() => {
      setTimeout(() => this.syncFooterWidths(), 100);
    });

    // Re-sync on window resize
    window.addEventListener("resize", () => this.syncFooterWidths());

    // Re-sync when fonts are loaded
    if (document.fonts) {
      document.fonts.ready.then(() => this.syncFooterWidths());
    }

    // Check if content is already in the main container (from static HTML)
    const hasContent =
      this.contentContainer &&
      this.contentContainer.innerHTML.trim().length > 50 &&
      !this.contentContainer.innerHTML.includes(
        "<!-- Content loaded dynamically -->",
      );

    const isDeepLink = currentPath !== "/" && currentPath !== "/index.html";
    const isHomepageFallback =
      isDeepLink && this.contentContainer.querySelector(".home-hero") !== null;

    if (hasContent && !isHomepageFallback) {
      // Content already loaded from static HTML, just initialize handlers
      console.log(
        "📍 Content already present from static HTML, skipping fetch",
      );
      this.currentPath = currentPath;

      // /documentation serves the first handbook page — canonicalize the URL
      if (currentPath === "/documentation") {
        const currentDoc = this.contentContainer.querySelector(
          ".article-toc a.is-current",
        );
        const canonical = currentDoc?.getAttribute("href");
        if (canonical && canonical !== "/documentation") {
          window.history.replaceState({ path: canonical }, "", canonical);
          this.currentPath = canonical;
        }
      }

      this.initializePageHandlers(this.currentPath);
      this.trackPageView(this.currentPath);
      return;
    }

    if (isDeepLink) {
      // We're on a deep link, load the appropriate content
      console.log("📍 Loading deep link page");
      this.loadPage(currentPath, false);
    } else {
      // Load home page by default
      console.log("📍 Loading home page");
      this.loadPage("/", false);
    }
  }

  /**
   * Sync footer left/right widths with header left/right widths
   */
  syncFooterWidths() {
    const headerLeft = document.querySelector(".title-bar-left");
    const headerRight = document.querySelector(".title-bar-right");
    const footerLeft = document.querySelector(".footer-left");
    const footerRight = document.querySelector(".footer-right");

    console.log("🔄 Syncing footer widths...");
    console.log("Header left:", headerLeft, "width:", headerLeft?.offsetWidth);
    console.log(
      "Header right:",
      headerRight,
      "width:",
      headerRight?.offsetWidth,
    );
    console.log("Footer left:", footerLeft);
    console.log("Footer right:", footerRight);

    if (headerLeft && headerRight) {
      const leftWidth = headerLeft.offsetWidth;
      // Set header right to be at least as wide as header left
      headerRight.style.minWidth = `${leftWidth}px`;
      console.log("✓ Set header-right min-width to:", leftWidth);
    }

    if (headerLeft && footerLeft) {
      const leftWidth = headerLeft.offsetWidth;
      footerLeft.style.width = `${leftWidth}px`;
      footerLeft.style.flexShrink = "0";
      footerLeft.style.minWidth = "unset";
      footerLeft.style.flexGrow = "0";
      console.log("✓ Set footer-left width to:", leftWidth);
    }

    if (headerRight && footerRight) {
      const rightWidth = headerRight.offsetWidth;
      footerRight.style.width = `${rightWidth}px`;
      footerRight.style.flexShrink = "0";
      footerRight.style.minWidth = "unset";
      footerRight.style.flexGrow = "0";
      console.log("✓ Set footer-right width to:", rightWidth);
    }
  }

  /**
   * Capture tracking parameters from URL and remove them from display
   */
  captureAndCleanTrackingParams() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    let hasTrackingParams = false;

    // Extract and store tracking parameters
    this.trackingParamNames.forEach((paramName) => {
      if (params.has(paramName)) {
        this.trackingParams[paramName] = params.get(paramName);
        params.delete(paramName);
        hasTrackingParams = true;
      }
    });

    // If we found tracking params, clean the URL
    if (hasTrackingParams) {
      const cleanUrl =
        url.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", cleanUrl);

      console.log("Tracking parameters:", this.trackingParams);
    }
  }

  /**
   * Get stored tracking parameters
   */
  getTrackingParams() {
    return { ...this.trackingParams };
  }

  /**
   * Track pageview with Plausible including UTM parameters
   */
  trackPageView(path) {
    // Build URL with UTM parameters for Plausible to detect campaign sources
    let url = path || window.location.pathname;

    if (Object.keys(this.trackingParams).length > 0) {
      const params = new URLSearchParams(this.trackingParams);
      url = `${url}?${params.toString()}`;
    }

    // Plausible requires full URL including domain
    const fullUrl = window.location.origin + url;

    const eventData = {
      url: fullUrl,
      // Also include as props for custom properties tracking
      props:
        Object.keys(this.trackingParams).length > 0
          ? this.trackingParams
          : undefined,
    };

    console.log("📊 Plausible pageview:", eventData);
    plausibleTrack("pageview", eventData);
  }

  /**
   * Track custom event with Plausible
   */
  trackEvent(eventName, customProps = {}) {
    const props = {
      ...this.trackingParams,
      ...customProps,
    };

    // Build URL with UTM parameters for Plausible to detect campaign sources
    let url = window.location.pathname;
    if (Object.keys(this.trackingParams).length > 0) {
      const params = new URLSearchParams(this.trackingParams);
      url = `${url}?${params.toString()}`;
    }

    // Plausible requires full URL including domain
    const fullUrl = window.location.origin + url;

    const eventData = {
      url: fullUrl,
      props: Object.keys(props).length > 0 ? props : undefined,
    };

    console.log(`📊 Plausible event "${eventName}":`, eventData);
    plausibleTrack(eventName, eventData);
  }

  /**
   * Determine if a URL is internal to this application
   */
  isInternalLink(url) {
    try {
      const link = new URL(url, window.location.origin);
      return link.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  /**
   * Handle click events on links
   */
  handleLinkClick(e) {
    // Find the closest <a> tag
    const link = e.target.closest("a");

    if (!link) return;

    // Respect browser-native behaviors like opening in a new tab/window.
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }

    if (link.hasAttribute("download")) {
      return;
    }

    const target = (link.getAttribute("target") || "").toLowerCase();
    if (target && target !== "_self") {
      return;
    }

    const href = link.getAttribute("href");

    // Handle hash links (anchor links on same page)
    if (href && href.startsWith("#")) {
      e.preventDefault();
      const targetId = href.substring(1);
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        // Update URL hash
        window.history.replaceState(null, "", href);
      }
      return;
    }

    // Ignore if no href or special links
    if (!href || href.startsWith("javascript:")) {
      return;
    }

    // Check if it's an internal link
    if (this.isInternalLink(href)) {
      e.preventDefault();

      const url = new URL(href, window.location.origin);
      const path = url.pathname;

      // Don't reload if we're already on this page
      if (path === this.currentPath) {
        return;
      }

      this.loadPage(path, true);
    }
    // External links will open normally (default behavior)
  }

  /**
   * Handle browser back/forward button clicks
   */
  handlePopState(e) {
    const path = window.location.pathname;

    this.loadPage(path, false);
  }

  /**
   * Load a page and update the UI
   */
  async loadPage(path, pushState = true) {
    try {
      // Show loading state (optional - can be enhanced later)
      this.showLoading();

      await this.refreshCurrentUser();
      this.updateAuthUI();

      // Separate pathname and query string
      const [pathname, queryString] = path.includes("?")
        ? path.split("?")
        : [path, ""];
      const fullPath = queryString ? `${pathname}?${queryString}` : pathname;

      // Get page content (from cache or fetch) - use pathname only
      const content = await this.getPageContent(pathname);

      // Extract main content if this is a full HTML document
      let contentToInsert = content;
      if (content.includes("<!DOCTYPE html>") || content.includes("<html")) {
        // Parse the HTML and extract just the main content
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        const main = doc.querySelector("main");
        if (main) {
          contentToInsert = main.innerHTML;
        }
      }

      // Update the main content container
      this.contentContainer.innerHTML = contentToInsert;

      // Update page title
      let pageTitle = PAGE_TITLES[pathname];

      // For articles, extract title from h1
      if (pathname.startsWith("/articles/") && pathname !== "/articles") {
        const h1 = this.contentContainer.querySelector("h1");
        if (h1) {
          pageTitle = `${h1.textContent} - Counterpunch`;
        }
      }

      // For documentation pages, extract title from h1
      if (
        pathname.startsWith("/documentation/") ||
        pathname === "/documentation"
      ) {
        const h1 = this.contentContainer.querySelector("h1");
        if (h1) {
          pageTitle = `${h1.textContent} - Counterpunch Documentation`;
        }
      }

      if (!pageTitle) {
        pageTitle = PAGE_TITLES["/"];
      }

      document.title = pageTitle;

      // Update browser URL and history - use full path with query string
      if (pushState) {
        window.history.pushState({ path: fullPath }, "", fullPath);
      }

      // Update current path to pathname only
      this.currentPath = pathname;

      // /documentation is the first handbook page — canonicalize the URL
      if (pathname === "/documentation") {
        const currentDoc = this.contentContainer.querySelector(
          ".article-toc a.is-current",
        );
        const canonical = currentDoc?.getAttribute("href");
        if (canonical && canonical !== "/documentation") {
          window.history.replaceState({ path: canonical }, "", canonical);
          this.currentPath = canonical;
        }
      }

      // Initialize page-specific functionality
      this.initializePageHandlers(this.currentPath);

      // Scroll to top or hash target
      if (window.location.hash) {
        // If there's a hash, scroll to it after a brief delay to ensure layout is ready
        requestAnimationFrame(() => {
          setTimeout(() => {
            const target = document.getElementById(
              window.location.hash.substring(1),
            );
            if (target) {
              target.scrollIntoView({ behavior: "smooth" });
            }
          }, 50);
        });
      } else {
        window.scrollTo(0, 0);
      }

      // Track pageview with Plausible including UTM parameters
      console.log("📍 About to track pageview for path:", path);
      this.trackPageView(path);

      // Hide loading state
      this.hideLoading();
    } catch (error) {
      console.error("Error loading page:", error);
      this.showError(error);
    }
  }

  /**
   * Get page content from cache or fetch it
   */
  async getPageContent(path) {
    // Check cache first
    if (this.pageCache.has(path)) {
      return this.pageCache.get(path);
    }

    // Determine the file path for the content
    let contentPath = path;

    // Convert root path to home
    if (path === "/" || path === "") {
      contentPath = "/pages/home";
    }
    // Handle articles overview page
    else if (path === "/articles") {
      contentPath = "/pages/articles";
    }
    // Handle individual article pages - load as SPA content
    else if (path.startsWith("/articles/")) {
      // Extract slug from path like /articles/slug-name/ or /articles/slug-name
      const slug = path.split("/").filter(Boolean)[1];
      contentPath = `/pages/article-${slug}`;
      console.log("📄 Article page - slug:", slug, "contentPath:", contentPath);
    }
    // Handle other pages
    else if (!path.includes(".")) {
      // If no extension, assume .html and look in pages directory
      contentPath = `/pages${path}`;
    }

    // Fetch the content (Wrangler Pages handles .html extension automatically)
    const response = await fetch(contentPath);

    if (!response.ok) {
      throw new Error(
        `Failed to load page: ${response.status} ${response.statusText}`,
      );
    }

    const content = await response.text();

    // Cache the content
    this.pageCache.set(path, content);

    return content;
  }

  /**
   * Show loading indicator
   */
  showLoading() {
    this.contentContainer.style.opacity = "0.5";
  }

  /**
   * Hide loading indicator
   */
  hideLoading() {
    this.contentContainer.style.opacity = "1";
  }

  /**
   * Show error message
   */
  showError(error) {
    this.contentContainer.innerHTML = `
      <div class="content-container error-message">
        <h1>Oops!</h1>
        <p>Failed to load page: ${error.message}</p>
        <p><a href="/">Return to home</a></p>
      </div>
    `;
    this.hideLoading();
  }

  /**
   * Clear the page cache (can be used for forced refresh)
   */
  clearCache() {
    this.pageCache.clear();
  }

  /**
   * Clear cache for a specific page
   */
  clearPageCache(path) {
    this.pageCache.delete(path);
  }

  /**
   * Refresh the user associated with the HttpOnly website session cookie.
   */
  async refreshCurrentUser() {
    try {
      const response = await fetch("/api/auth/me", {
        credentials: "include",
      });
      if (!response.ok) {
        this.currentUser = null;
        return null;
      }

      const data = await response.json();
      this.currentUser = data.authenticated
        ? { ...data.user, userId: data.user.id }
        : null;
      return this.currentUser;
    } catch (error) {
      console.error("Failed to refresh website authentication:", error);
      this.currentUser = null;
      return null;
    }
  }

  /**
   * Return the server-verified user cached from the HttpOnly session cookie.
   */
  getSessionUser() {
    return this.currentUser;
  }

  /**
   * Update the auth UI in the title bar
   */
  updateAuthUI() {
    const authSection = document.getElementById("authSection");
    if (!authSection) return;

    const user = this.currentUser;

    if (user) {
      // User is logged in
      authSection.innerHTML = `
        <div class="user-menu">
          <a href="/account" class="user-account-link">
            <span class="material-symbols-outlined">account_circle</span>
            <span>${user.email}</span>
          </a>
          <button class="button-base auth-button" onclick="window.appNavigator.logout()">Log Out</button>
        </div>
      `;

      this.updateAdminDashboardLink();
    } else {
      // User is not logged in
      authSection.innerHTML = `
        <a href="/login" class="button-base auth-button">
          <span class="material-symbols-outlined">account_circle</span>
          <span>Sign In</span>
        </a>
      `;

      this.isAdminUser = false;
      this.updateAdminDashboardLink();
    }

    // Re-sync footer widths after auth UI changes (header right width may have changed)
    requestAnimationFrame(() => {
      setTimeout(() => this.syncFooterWidths(), 50);
    });
  }

  getAdminNavLink() {
    const nav = document.querySelector(".title-bar-nav");
    if (!nav) return null;

    let link = document.getElementById("adminDashboardNavLink");
    if (!link) {
      link = document.createElement("a");
      link.id = "adminDashboardNavLink";
      link.href = "/admin/dashboard";
      link.textContent = "Dashboard";
      link.style.display = "none";
      nav.appendChild(link);
    }

    return link;
  }

  async updateAdminDashboardLink() {
    const link = this.getAdminNavLink();
    if (!link) return;

    if (!this.currentUser) {
      link.style.display = "none";
      this.isAdminUser = false;
      return;
    }

    try {
      const response = await fetch("/api/admin/status", {
        credentials: "include",
      });

      if (!response.ok) {
        link.style.display = "none";
        this.isAdminUser = false;
        return;
      }

      const data = await response.json();
      this.isAdminUser = !!data.isAdmin;
      link.style.display = this.isAdminUser ? "" : "none";
    } catch (error) {
      console.error("Failed to resolve admin status:", error);
      link.style.display = "none";
      this.isAdminUser = false;
    }
  }

  /**
   * Log out the current user
   */
  async logout() {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`logout failed: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to log out:", error);
      return;
    }

    this.currentUser = null;

    // Track logout event
    this.trackEvent("logout");

    // Update UI
    this.updateAuthUI();

    // Reload current page
    await this.loadPage(this.currentPath, false);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.currentUser !== null;
  }

  /**
   * Redirect to login with return path
   */
  redirectToLogin() {
    const returnTo = encodeURIComponent(this.currentPath);
    this.loadPage(`/login?returnTo=${returnTo}`, true);
  }

  redirectToLoginPath(returnToPath) {
    const returnTo = encodeURIComponent(returnToPath);
    this.loadPage(`/login?returnTo=${returnTo}`, true);
  }

  /**
   * Show authentication error message
   */
  showAuthError(errorCode) {
    const errorMessages = {
      invalid_token: "Invalid magic link. Please request a new one.",
      expired_token: "This magic link has expired. Please request a new one.",
      user_not_found: "User not found. Please try again.",
      verification_failed: "Verification failed. Please try again.",
    };

    const message =
      errorMessages[errorCode] || "An authentication error occurred.";

    // Create a temporary notification
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 60px;
      right: 20px;
      background: rgba(255, 68, 68, 0.95);
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: var(--font-families-sans);
      font-size: 14px;
      max-width: 400px;
    `;
    notification.textContent = `✗ ${message}`;

    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transition = "opacity 0.3s ease";
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }

  /**
   * Initialize page-specific handlers after content is loaded
   */
  initializePageHandlers(path) {
    // Normalize path by removing trailing slash
    const normalizedPath =
      path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;

    // Handle article pages (in-page Contents) and documentation (handbook Contents)
    if (normalizedPath.startsWith("/articles/")) {
      requestAnimationFrame(() => {
        this.initializeArticleTOC();
      });
    } else if (
      normalizedPath.startsWith("/documentation/") ||
      normalizedPath === "/documentation"
    ) {
      requestAnimationFrame(() => {
        this.refreshDocumentationScreenshots();
        this.scrollDocumentationTocIntoView();
      });
    }
    // Handle login page
    else if (normalizedPath === "/login") {
      // If user is already logged in, redirect them back immediately
      if (this.currentUser) {
        const params = new URLSearchParams(window.location.search);
        const returnTo = params.get("returnTo");
        const messageContainer = document.getElementById("messageContainer");
        const loginForm = document.getElementById("loginForm");

        // Hide the login form
        if (loginForm) {
          loginForm.style.display = "none";
        }

        // Show message
        if (messageContainer) {
          messageContainer.innerHTML = `<div class="message success">✓ You are already logged in. Redirecting in 5 seconds...</div>`;
        }

        // Redirect after 5 seconds
        setTimeout(() => {
          if (returnTo) {
            window.location.href = returnTo;
          } else {
            this.navigateTo("/account");
          }
        }, 5000);
        return;
      }
      this.initializeLoginForm();
    }
    // Handle invite page
    else if (normalizedPath === "/invite") {
      this.initializeInvitePage();
    }
    // Handle transfer page
    else if (normalizedPath === "/transfer") {
      this.initializeTransferPage();
    }
    // Handle account page
    else if (normalizedPath === "/account") {
      this.initializeAccountPage();
    }
    // Handle admin dashboard page
    else if (normalizedPath === "/admin/dashboard") {
      this.initializeAdminDashboardPage();
    }
    // Handle admin errors page
    else if (normalizedPath === "/admin/errors") {
      this.initializeAdminErrorsPage();
    }
    // Handle pricing page
    else if (normalizedPath === "/pricing") {
      this.initializePricingPage();
    }
    // Handle home page — load editor version info
    else if (normalizedPath === "/") {
      this.loadEditorVersions();
    }
  }

  async fetchAdminStatus() {
    const statusResponse = await fetch("/api/admin/status", {
      credentials: "include",
    });
    return statusResponse.ok ? await statusResponse.json() : null;
  }

  getAdminAppChannel() {
    const stored = localStorage.getItem("admin_app_channel");
    if (stored === "preview" || stored === "production") {
      return stored;
    }

    return "production";
  }

  setAdminAppChannel(channel) {
    if (channel !== "preview" && channel !== "production") {
      return;
    }

    localStorage.setItem("admin_app_channel", channel);
  }

  renderAdminAppChannelSwitch(mountId, onChange) {
    const mount = document.getElementById(mountId);
    if (!mount) return;

    const selectedChannel = this.getAdminAppChannel();
    mount.innerHTML = `
      <div class="admin-channel-switch" role="group" aria-label="App channel filter">
        <span class="admin-channel-switch-label">App:</span>
        <button type="button" class="admin-channel-button ${selectedChannel === "production" ? "is-active" : ""}" data-channel="production">Production</button>
        <button type="button" class="admin-channel-button ${selectedChannel === "preview" ? "is-active" : ""}" data-channel="preview">Preview</button>
      </div>
    `;

    const buttons = Array.from(mount.querySelectorAll(".admin-channel-button"));
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const nextChannel = button.getAttribute("data-channel");
        if (!nextChannel || nextChannel === this.getAdminAppChannel()) {
          return;
        }

        this.setAdminAppChannel(nextChannel);
        if (onChange) {
          onChange(nextChannel);
        }
      });
    });
  }

  async fetchAdminDashboardOverview(
    days = 60,
    channel = this.getAdminAppChannel(),
  ) {
    const query = new URLSearchParams({
      days: String(days),
      channel,
    });

    const response = await fetch(
      `/api/admin/dashboard/overview?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load admin dashboard overview");
    }

    return await response.json();
  }

  renderAdminRoomHealth(roomHealth) {
    const roomHealthEl = document.getElementById("adminDashboardRoomHealth");
    if (!roomHealthEl) return;

    const totals = roomHealth?.totals || {};
    const staleRooms = roomHealth?.staleRooms || [];
    const oldestEdit = totals.oldestUncheckpointedEditAt
      ? new Date(totals.oldestUncheckpointedEditAt).toLocaleString()
      : "None";

    roomHealthEl.innerHTML = `
      <div class="admin-errors-summary-grid admin-room-health-grid">
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Rooms</div>
          <div class="admin-errors-metric-value">${Number(totals.totalRooms) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Pending / failed bootstrap</div>
          <div class="admin-errors-metric-value">${Number(totals.pendingBootstrapRooms) || 0} / ${Number(totals.bootstrapFailedRooms) || 0}</div>
        </div>
        <div class="admin-errors-metric">
          <div class="admin-errors-metric-label">Highest room version</div>
          <div class="admin-errors-metric-value">${Number(totals.highestRoomVersion) || 0}</div>
        </div>
        <div class="admin-errors-metric admin-room-health-wide">
          <div class="admin-errors-metric-label">Oldest uncheckpointed edit</div>
          <div class="admin-errors-metric-value admin-room-health-date">${this.escapeHtml(oldestEdit)}</div>
        </div>
      </div>
      <div class="admin-room-health-table-wrap">
        <table class="admin-room-health-table">
          <thead>
            <tr>
              <th>Room</th>
              <th>State</th>
              <th>Version</th>
              <th>Glyphs</th>
              <th>Updated</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            ${staleRooms
              .map((room) => {
                const updatedAt = room.updatedAt
                  ? new Date(room.updatedAt).toLocaleString()
                  : "Unknown";
                return `
                  <tr>
                    <td>
                      <div>${this.escapeHtml(room.name || room.id || "Unknown")}</div>
                      <div class="admin-room-health-id">${this.escapeHtml(room.id || "")}</div>
                    </td>
                    <td>${this.escapeHtml(room.lifecycleState || "unknown")}</td>
                    <td>${Number(room.currentRoomVersion) || 0}</td>
                    <td>${Number(room.glyphCount) || 0}</td>
                    <td>${this.escapeHtml(updatedAt)}</td>
                    <td>
                      <button type="button" class="admin-room-health-action" data-room-compact-id="${this.escapeHtml(room.id || "")}">Request compaction</button>
                    </td>
                  </tr>
                `;
              })
              .join("")}
          </tbody>
        </table>
        ${staleRooms.length === 0 ? '<p class="admin-room-health-empty">No active rooms with pending versions.</p>' : ""}
      </div>
    `;

    Array.from(roomHealthEl.querySelectorAll("[data-room-compact-id]")).forEach(
      (button) => {
        button.addEventListener("click", async () => {
          const roomId = button.getAttribute("data-room-compact-id");
          if (!roomId) return;

          button.disabled = true;
          button.textContent = "Requesting...";

          try {
            const response = await fetch(
              `/api/admin/cloud/rooms/${encodeURIComponent(roomId)}/compact`,
              {
                method: "POST",
                credentials: "include",
              },
            );
            const payload = await response.json().catch(() => null);
            if (!response.ok) {
              throw new Error(payload?.error || "Compaction request failed");
            }
            button.textContent = payload?.compacted ? "Compacted" : "No tail";
          } catch (error) {
            console.error("Room compaction request failed:", error);
            button.disabled = false;
            button.textContent = "Retry compaction";
          }
        });
      },
    );
  }

  async fetchAdminMembershipLookup(query) {
    const params = new URLSearchParams({ query });
    const response = await fetch(
      `/api/admin/subscription/lookup?${params.toString()}`,
      {
        credentials: "include",
      },
    );

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to look up user");
    }

    return data;
  }

  async fetchAdminMembershipOverride(payload) {
    const response = await fetch("/api/admin/subscription/override", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to update membership override");
    }

    return data;
  }

  renderAdminMembershipTools(messageMarkup = "") {
    const toolsEl = document.getElementById("adminMembershipTools");
    if (!toolsEl) {
      return;
    }

    const result = this.adminMembershipLookupResult;
    const productOptions = Object.values(this.getPricingProducts())
      .map((product) => {
        const isSelected =
          result?.membershipOverride?.productId === product.id ||
          (!result?.membershipOverride?.productId && product.id === "basic");
        return `<option value="${this.escapeHtml(product.id)}" ${
          isSelected ? "selected" : ""
        }>${this.escapeHtml(product.name)}</option>`;
      })
      .join("");

    const effectiveMembership = result?.effectiveMembership;
    const stripeSubscription = result?.stripeSubscription;
    const membershipOverride = result?.membershipOverride;
    const effectiveLabel = effectiveMembership
      ? `${this.escapeHtml(effectiveMembership.planName)} (${this.escapeHtml(
          effectiveMembership.source,
        )})`
      : "No active membership";
    const effectiveEndsAt = effectiveMembership?.endsAt
      ? new Date(effectiveMembership.endsAt * 1000).toLocaleDateString()
      : null;
    const overrideEndsAt = membershipOverride?.endsAt
      ? new Date(membershipOverride.endsAt * 1000).toLocaleDateString()
      : "No end date";
    const defaultEndsAtValue = membershipOverride?.endsAt
      ? new Date(membershipOverride.endsAt * 1000).toISOString().slice(0, 10)
      : "";

    toolsEl.innerHTML = `
      <form id="adminMembershipLookupForm" style="display: grid; gap: 10px; margin-bottom: 16px;">
        <label for="adminMembershipLookupQuery">User ID or Email</label>
        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
          <input
            id="adminMembershipLookupQuery"
            name="query"
            type="text"
            placeholder="user_123 or person@example.com"
            value="${this.escapeHtml(result?.user?.email || result?.user?.id || "")}"
            style="flex: 1 1 280px;"
            required
          />
          <button type="submit" class="button-base auth-button">Look Up User</button>
        </div>
      </form>
      <div id="adminMembershipMessage">${messageMarkup}</div>
      ${
        result
          ? `
        <div class="admin-membership-result" style="display: grid; gap: 16px;">
          <div>
            <div><strong>User:</strong> ${this.escapeHtml(result.user.email)}</div>
            <div><strong>User ID:</strong> ${this.escapeHtml(result.user.id)}</div>
            <div><strong>Effective membership:</strong> ${effectiveLabel}</div>
            ${
              effectiveEndsAt
                ? `<div><strong>Access until:</strong> ${this.escapeHtml(effectiveEndsAt)}</div>`
                : ""
            }
          </div>
          <div>
            <div><strong>Stripe subscription:</strong> ${
              stripeSubscription
                ? `${this.escapeHtml(stripeSubscription.planName)} (${this.escapeHtml(
                    stripeSubscription.status,
                  )})`
                : "None"
            }</div>
            <div><strong>Membership override:</strong> ${
              membershipOverride
                ? `${this.escapeHtml(membershipOverride.productId)} through ${this.escapeHtml(
                    overrideEndsAt,
                  )}`
                : "None"
            }</div>
            ${
              membershipOverride?.note
                ? `<div><strong>Note:</strong> ${this.escapeHtml(membershipOverride.note)}</div>`
                : ""
            }
          </div>
          <form id="adminMembershipOverrideForm" style="display: grid; gap: 10px;">
            <input type="hidden" name="userQuery" value="${this.escapeHtml(result.user.id)}" />
            <label for="adminMembershipProduct">Membership</label>
            <select id="adminMembershipProduct" name="productId">${productOptions}</select>
            <label for="adminMembershipEndsAt">End date</label>
            <input id="adminMembershipEndsAt" name="endsAt" type="date" value="${this.escapeHtml(defaultEndsAtValue)}" />
            <label for="adminMembershipNote">Admin note</label>
            <textarea id="adminMembershipNote" name="note" rows="3" placeholder="Optional note">${this.escapeHtml(
              membershipOverride?.note || "",
            )}</textarea>
            <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
              <button type="submit" class="button-base auth-button">Save Override</button>
              <button type="button" class="button-base subscription-button danger" id="adminMembershipRemoveButton" ${
                membershipOverride ? "" : "disabled"
              }>Remove Override</button>
            </div>
          </form>
        </div>
      `
          : '<p style="color: var(--text-secondary); margin: 0;">Look up a user to inspect and manage an override.</p>'
      }
    `;

    const lookupForm = document.getElementById("adminMembershipLookupForm");
    if (lookupForm) {
      lookupForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const formData = new FormData(lookupForm);
        const query = String(formData.get("query") || "").trim();
        const messageEl = document.getElementById("adminMembershipMessage");
        if (!query) {
          return;
        }

        if (messageEl) {
          messageEl.innerHTML =
            '<div class="message info">Looking up user...</div>';
        }

        try {
          this.adminMembershipLookupResult =
            await this.fetchAdminMembershipLookup(query);
          this.renderAdminMembershipTools(
            '<div class="message success">User loaded.</div>',
          );
        } catch (error) {
          this.renderAdminMembershipTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const overrideForm = document.getElementById("adminMembershipOverrideForm");
    if (overrideForm) {
      overrideForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const formData = new FormData(overrideForm);

        try {
          await this.fetchAdminMembershipOverride({
            action: "upsert",
            userQuery: String(formData.get("userQuery") || "").trim(),
            productId: String(formData.get("productId") || "").trim(),
            endsAt: String(formData.get("endsAt") || "").trim(),
            note: String(formData.get("note") || "").trim(),
          });
          this.adminMembershipLookupResult =
            await this.fetchAdminMembershipLookup(
              String(formData.get("userQuery") || "").trim(),
            );
          this.renderAdminMembershipTools(
            '<div class="message success">Override saved.</div>',
          );
        } catch (error) {
          this.renderAdminMembershipTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const removeButton = document.getElementById("adminMembershipRemoveButton");
    if (removeButton) {
      removeButton.addEventListener("click", async () => {
        if (!result?.user?.id) {
          return;
        }

        try {
          await this.fetchAdminMembershipOverride({
            action: "remove",
            userQuery: result.user.id,
          });
          this.adminMembershipLookupResult =
            await this.fetchAdminMembershipLookup(result.user.id);
          this.renderAdminMembershipTools(
            '<div class="message success">Override removed.</div>',
          );
        } catch (error) {
          this.renderAdminMembershipTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }
  }

  async fetchAdminCloudOverride({ action, userQuery, note }) {
    const response = await fetch("/api/admin/cloud/override", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, userQuery, note }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to update cloud override");
    }
    return data;
  }

  renderAdminCloudTools(messageMarkup = "") {
    const toolsEl = document.getElementById("adminCloudTools");
    if (!toolsEl) return;

    const result = this.adminCloudLookupResult;

    toolsEl.innerHTML = `
      <form id="adminCloudLookupForm" style="display: grid; gap: 10px; margin-bottom: 16px;">
        <label for="adminCloudLookupQuery">User ID or Email</label>
        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
          <input
            id="adminCloudLookupQuery"
            name="query"
            type="text"
            placeholder="user_123 or person@example.com"
            value="${this.escapeHtml(result?.user?.email || result?.user?.id || "")}"
            style="flex: 1 1 280px;"
            required
          />
          <button type="submit" class="button-base auth-button">Look Up User</button>
        </div>
      </form>
      <div id="adminCloudMessage">${messageMarkup}</div>
      ${
        result
          ? `
        <div class="admin-membership-result" style="display: grid; gap: 16px;">
          <div>
            <div><strong>User:</strong> ${this.escapeHtml(result.user.email)}</div>
            <div><strong>User ID:</strong> ${this.escapeHtml(result.user.id)}</div>
            <div><strong>Cloud hosting:</strong> ${result.cloudHostingEnabled ? "✓ Enabled" : "✗ Disabled"}</div>
            ${result.override?.note ? `<div><strong>Note:</strong> ${this.escapeHtml(result.override.note)}</div>` : ""}
          </div>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            <form id="adminCloudGrantForm" style="display: contents;">
              <input type="hidden" name="userQuery" value="${this.escapeHtml(result.user.id)}" />
              <label for="adminCloudNote" style="align-self: center;">Note</label>
              <input id="adminCloudNote" name="note" type="text" placeholder="Optional note" value="${this.escapeHtml(result.override?.note || "")}" style="flex: 1 1 180px;" />
              <button type="submit" class="button-base auth-button">Grant Access</button>
              <button type="button" class="button-base subscription-button danger" id="adminCloudRevokeButton" ${result.cloudHostingEnabled ? "" : "disabled"}>Revoke Access</button>
            </form>
          </div>
        </div>
      `
          : '<p style="color: var(--text-secondary); margin: 0;">Look up a user to grant or revoke cloud hosting access.</p>'
      }
    `;

    const lookupForm = document.getElementById("adminCloudLookupForm");
    if (lookupForm) {
      lookupForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const query = String(
          new FormData(lookupForm).get("query") || "",
        ).trim();
        const msgEl = document.getElementById("adminCloudMessage");
        if (!query) return;
        if (msgEl)
          msgEl.innerHTML =
            '<div class="message info">Looking up user...</div>';
        try {
          // Reuse membership lookup to get user info, then check eligibility
          const lookup = await this.fetchAdminMembershipLookup(query);
          // Fetch current cloud eligibility for this user by granting then immediately reading
          // Use the override endpoint with action=grant to probe — no, instead fetch eligibility
          const eligResp = await fetch(
            `/api/cloud/eligibility?userId=${encodeURIComponent(lookup.user.id)}`,
            { credentials: "include" },
          );
          const elig = eligResp.ok
            ? await eligResp.json()
            : { cloudHostingEnabled: false };
          this.adminCloudLookupResult = {
            user: lookup.user,
            cloudHostingEnabled: elig.cloudHostingEnabled,
            override: null,
          };
          this.renderAdminCloudTools(
            '<div class="message success">User loaded.</div>',
          );
        } catch (error) {
          this.renderAdminCloudTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const grantForm = document.getElementById("adminCloudGrantForm");
    if (grantForm) {
      grantForm.addEventListener("submit", async (event) => {
        event.preventDefault();
        const fd = new FormData(grantForm);
        const userQuery = String(fd.get("userQuery") || "").trim();
        const note = String(fd.get("note") || "").trim();
        try {
          const res = await this.fetchAdminCloudOverride({
            action: "grant",
            userQuery,
            note,
          });
          this.adminCloudLookupResult = {
            user: result.user,
            cloudHostingEnabled: res.cloudHostingEnabled,
            override: { note },
          };
          this.renderAdminCloudTools(
            '<div class="message success">Cloud access granted.</div>',
          );
        } catch (error) {
          this.renderAdminCloudTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }

    const revokeButton = document.getElementById("adminCloudRevokeButton");
    if (revokeButton) {
      revokeButton.addEventListener("click", async () => {
        if (!result?.user?.id) return;
        try {
          const res = await this.fetchAdminCloudOverride({
            action: "revoke",
            userQuery: result.user.id,
          });
          this.adminCloudLookupResult = {
            user: result.user,
            cloudHostingEnabled: res.cloudHostingEnabled,
            override: null,
          };
          this.renderAdminCloudTools(
            '<div class="message success">Cloud access revoked.</div>',
          );
        } catch (error) {
          this.renderAdminCloudTools(
            `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          );
        }
      });
    }
  }

  async fetchAdminErrorDetails(days = 60, channel = this.getAdminAppChannel()) {
    const query = new URLSearchParams({
      days: String(days),
      channel,
    });

    const response = await fetch(
      `/api/admin/errors/details?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load admin error details");
    }

    return await response.json();
  }

  async fetchAdminErrorEvents(
    fingerprint,
    days = 60,
    limit = 200,
    channel = this.getAdminAppChannel(),
  ) {
    const query = new URLSearchParams({
      fingerprint,
      days: String(days),
      limit: String(limit),
      channel,
    });

    const response = await fetch(
      `/api/admin/errors/events?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load admin error events");
    }

    return await response.json();
  }

  async fetchAdminMuteErrorSignature(
    fingerprint,
    channel = this.getAdminAppChannel(),
  ) {
    const response = await fetch("/api/admin/errors/mute", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fingerprint,
        channel,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to mute error signature");
    }

    return await response.json();
  }

  async fetchAdminUnmuteErrorSignature(
    fingerprint,
    channel = this.getAdminAppChannel(),
  ) {
    const response = await fetch("/api/admin/errors/unmute", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fingerprint,
        channel,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to unmute error signature");
    }

    return await response.json();
  }

  async fetchAdminMutedErrorSignatures(channel = this.getAdminAppChannel()) {
    const query = new URLSearchParams({
      channel,
    });

    const response = await fetch(
      `/api/admin/errors/muted?${query.toString()}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Failed to load muted error signatures");
    }

    return await response.json();
  }

  buildAdminErrorHandoff(issue, eventsResponse, channel) {
    const triageInstruction =
      "Analyze this error handoff and decide whether it's an issue that we need to react on in our own code or whether this is an issue caused by for example browser plugins that users have installed that cause noise that we can safely ignore. Don't implement any noise filters, just analyze and possibly propose a fix or suggest to ignore as noise.";
    const investigationInstruction =
      "Use state-manager.ts as the primary entry point for global capture/reporting, then follow stack-mapper.ts for mapped stacks, wasm-init.ts and coi-serviceworker.js for startup failures, and ai-assistant.ts plus mcp-transport.ts for feature-specific error paths.";

    const events = Array.isArray(eventsResponse?.events)
      ? eventsResponse.events
      : [];

    const compactEvents = events.slice(0, 1).map((eventRow) => ({
      id: eventRow.id || null,
      createdAt: eventRow.createdAt || null,
      message: eventRow.errorMessage || null,
      stack: eventRow.errorStack || null,
      source: eventRow.source || null,
      url: eventRow.url || null,
      userAgent: eventRow.userAgent || null,
      userEmail: eventRow.userEmail || null,
      userId: eventRow.userId || null,
      appVersion: eventRow.appVersion || null,
      buildHash: eventRow.buildHash || null,
      sessionId: eventRow.sessionId || null,
      payload: eventRow.payload || null,
    }));

    const handoffPayload = {
      type: "counterpunch-error-handoff-v1",
      generatedAt: new Date().toISOString(),
      request:
        "Analyze this production error and propose a concrete fix with likely root cause.",
      issue: {
        fingerprint: issue?.fingerprint || eventsResponse?.fingerprint || null,
        message: issue?.message || null,
        eventCountLast60d:
          issue?.eventCount || eventsResponse?.totalEvents || 0,
        firstSeen: issue?.firstSeen || null,
        lastSeen: issue?.lastSeen || null,
        users: issue?.users || [],
        isMuted: Boolean(issue?.isMuted || eventsResponse?.isMuted),
      },
      environment: {
        appChannel: channel || eventsResponse?.channel || "production",
      },
      eventsSummary: {
        totalEvents: eventsResponse?.totalEvents || 0,
        returnedEvents: eventsResponse?.returnedEvents || events.length,
      },
      recentEvents: compactEvents,
    };

    return `${triageInstruction}\n${investigationInstruction}\nCOUNTERPUNCH_ERROR_HANDOFF::${JSON.stringify(handoffPayload)}`;
  }

  async copyTextToClipboard(value) {
    if (!value) {
      throw new Error("Nothing to copy");
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "readonly");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  async initializeAdminDashboardPage() {
    const messageEl = document.getElementById("adminDashboardMessage");
    const metricsEl = document.getElementById("adminDashboardMetrics");
    const timelineEl = document.getElementById("adminDashboardTimeline");
    const roomHealthEl = document.getElementById("adminDashboardRoomHealth");
    const linksEl = document.getElementById("adminDashboardLinks");
    const membershipToolsEl = document.getElementById("adminMembershipTools");
    const cloudToolsEl = document.getElementById("adminCloudTools");

    if (
      !messageEl ||
      !metricsEl ||
      !timelineEl ||
      !roomHealthEl ||
      !linksEl ||
      !membershipToolsEl ||
      !cloudToolsEl
    ) {
      return;
    }

    this.renderAdminAppChannelSwitch("adminDashboardChannelSwitch", () => {
      this.initializeAdminDashboardPage();
    });

    const selectedChannel = this.getAdminAppChannel();

    const user = this.getSessionUser();
    if (!user) {
      const returnTo = encodeURIComponent("/admin/dashboard");
      this.loadPage(`/login?returnTo=${returnTo}`, true);
      return;
    }

    messageEl.innerHTML =
      '<div class="message info">Loading dashboard data...</div>';
    this.renderAdminMembershipTools();
    this.renderAdminCloudTools();

    try {
      const status = await this.fetchAdminStatus();

      if (!status?.isAdmin) {
        messageEl.innerHTML =
          '<div class="message error">✗ You do not have access to this dashboard.</div>';
        metricsEl.innerHTML = "";
        timelineEl.innerHTML = "";
        roomHealthEl.innerHTML = "";
        linksEl.innerHTML = "";
        membershipToolsEl.innerHTML = "";
        cloudToolsEl.innerHTML = "";
        return;
      }

      const overview = await this.fetchAdminDashboardOverview(
        60,
        selectedChannel,
      );

      const timeline = overview.timeline || [];
      const maxCount = Math.max(1, ...timeline.map((item) => item.count || 0));

      metricsEl.innerHTML = `
        <div class="admin-errors-summary-grid">
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Errors (60d)</div>
            <div class="admin-errors-metric-value">${overview.totals.totalEvents}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Unique error issues</div>
            <div class="admin-errors-metric-value">${overview.totals.uniqueFingerprints}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Users affected</div>
            <div class="admin-errors-metric-value">${overview.totals.usersAffected}</div>
          </div>
        </div>
      `;

      timelineEl.innerHTML = `
        <div class="admin-errors-chart">
          ${timeline
            .map((entry) => {
              const height = Math.max(
                2,
                Math.round((entry.count / maxCount) * 96),
              );
              return `
                <div class="admin-errors-bar-wrap" title="${entry.day}: ${entry.count}">
                  <div class="admin-errors-bar" style="height:${height}px"></div>
                </div>
              `;
            })
            .join("")}
        </div>
        <div class="admin-errors-chart-labels">
          <span>${overview.startDate}</span>
          <span>${overview.endDate}</span>
        </div>
      `;

      this.renderAdminRoomHealth(overview.cloudRoomHealth);

      linksEl.innerHTML = `
        <p style="color: var(--text-secondary); margin: 0 0 10px 0;">
          Showing: <strong>${this.escapeHtml(selectedChannel)}</strong>
        </p>
        <a href="/admin/errors" class="button-base auth-button">Open Error Details</a>
      `;

      messageEl.innerHTML = "";
      this.renderAdminMembershipTools();
    } catch (error) {
      console.error("Admin dashboard failed:", error);
      messageEl.innerHTML =
        '<div class="message error">✗ Failed to load dashboard data. Please try again.</div>';
    }
  }

  async initializeAdminErrorsPage() {
    const messageEl = document.getElementById("adminErrorsMessage");
    const summaryEl = document.getElementById("adminErrorsSummary");
    const issuesEl = document.getElementById("adminErrorsIssues");
    const mutedEl = document.getElementById("adminMutedSignatures");
    const eventsEl = document.getElementById("adminErrorEvents");

    if (!messageEl || !summaryEl || !issuesEl || !mutedEl || !eventsEl) {
      return;
    }

    this.renderAdminAppChannelSwitch("adminErrorsChannelSwitch", () => {
      this.initializeAdminErrorsPage();
    });

    const selectedChannel = this.getAdminAppChannel();
    const params = new URLSearchParams(window.location.search);
    const requestedFingerprint = (params.get("fingerprint") || "").trim();
    const isFocusedIssueView = Boolean(requestedFingerprint);

    const applyFocusedIssueLayout = () => {
      const introEl = document.getElementById("adminErrorsIntro");
      const backLinkEl = document.getElementById("adminErrorsBackLink");
      const channelSwitchEl = document.getElementById(
        "adminErrorsChannelSwitch",
      );

      if (introEl) {
        introEl.style.display = "none";
      }
      if (backLinkEl) {
        backLinkEl.style.display = "none";
      }
      if (channelSwitchEl) {
        channelSwitchEl.style.display = "none";
      }

      const accountSections = Array.from(
        document.querySelectorAll(".account-layout .account-section"),
      );
      accountSections.forEach((section) => {
        section.classList.add("admin-errors-hidden-for-focus");
      });

      const eventsSection = eventsEl.closest(".account-section");
      if (eventsSection) {
        eventsSection.classList.remove("admin-errors-hidden-for-focus");
      }
    };

    const user = this.getSessionUser();
    if (!user) {
      const returnTo = encodeURIComponent("/admin/errors");
      this.loadPage(`/login?returnTo=${returnTo}`, true);
      return;
    }

    messageEl.innerHTML =
      '<div class="message info">Loading error details...</div>';

    try {
      const status = await this.fetchAdminStatus();

      if (!status?.isAdmin) {
        messageEl.innerHTML =
          '<div class="message error">✗ You do not have access to this page.</div>';
        summaryEl.innerHTML = "";
        issuesEl.innerHTML = "";
        mutedEl.innerHTML = "";
        eventsEl.innerHTML = "";
        return;
      }

      const [overview, mutedResponse] = await Promise.all([
        this.fetchAdminErrorDetails(60, selectedChannel),
        this.fetchAdminMutedErrorSignatures(selectedChannel),
      ]);

      const issues = overview.issues || [];
      const issuesByFingerprint = new Map(
        issues.map((issue) => [issue.fingerprint, issue]),
      );
      const mutedByFingerprint = new Map(
        (mutedResponse?.muted || []).map((entry) => [entry.fingerprint, entry]),
      );

      issues.forEach((issue) => {
        if (mutedByFingerprint.has(issue.fingerprint)) {
          issue.isMuted = true;
        }
      });
      const visibleIssues = issues.filter((issue) => !issue?.isMuted);

      summaryEl.innerHTML = `
        <p style="color: var(--text-secondary); margin: 0 0 10px 0;">
          Showing: <strong>${this.escapeHtml(selectedChannel)}</strong>
        </p>
        <div class="admin-errors-summary-grid">
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Total events (60d)</div>
            <div class="admin-errors-metric-value">${overview.totals.totalEvents}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Unique issues</div>
            <div class="admin-errors-metric-value">${overview.totals.uniqueFingerprints}</div>
          </div>
          <div class="admin-errors-metric">
            <div class="admin-errors-metric-label">Users affected</div>
            <div class="admin-errors-metric-value">${overview.totals.usersAffected}</div>
          </div>
        </div>
      `;

      if (isFocusedIssueView) {
        applyFocusedIssueLayout();
      }

      const renderMutedSignatures = () => {
        const mutedList = Array.from(mutedByFingerprint.values()).sort(
          (a, b) => (Number(b.mutedAt) || 0) - (Number(a.mutedAt) || 0),
        );

        if (!mutedList.length) {
          mutedEl.innerHTML =
            '<p style="color: var(--text-secondary);">No muted signatures for this channel.</p>';
          return;
        }

        mutedEl.innerHTML = `
          <div class="admin-errors-muted-list">
            ${mutedList
              .map((entry) => {
                const mutedAt = entry.mutedAt
                  ? new Date(entry.mutedAt).toLocaleString()
                  : "Unknown";
                const lastSeen = entry.lastSeen
                  ? new Date(entry.lastSeen).toLocaleString()
                  : "No events recorded";
                return `
                  <div class="admin-errors-muted-card" data-error-fingerprint="${this.escapeHtml(entry.fingerprint)}">
                    <div class="admin-errors-issue-message">${this.escapeHtml(entry.message || "Unknown error")}</div>
                    <div class="admin-errors-issue-meta">
                      <span>Fingerprint: ${this.escapeHtml(entry.fingerprint)}</span>
                      <span>Muted at: ${this.escapeHtml(mutedAt)}</span>
                      <span>Last seen: ${this.escapeHtml(lastSeen)}</span>
                    </div>
                    <div class="admin-errors-issue-meta">
                      <span>Events: ${Number(entry.totalEvents) || 0}</span>
                      <span>Muted by: ${this.escapeHtml(entry.mutedByEmail || entry.mutedByUserId || "Unknown")}</span>
                    </div>
                    <div class="admin-errors-issue-actions">
                      <a href="/admin/errors?fingerprint=${encodeURIComponent(entry.fingerprint)}" class="button-base auth-button admin-errors-issue-action admin-errors-light-button" target="_blank" rel="noopener noreferrer">Open in New Tab</a>
                      <button type="button" class="admin-errors-mute-button" data-error-action="unmute-muted-list" data-error-fingerprint="${this.escapeHtml(entry.fingerprint)}">Unmute Signature</button>
                    </div>
                  </div>
                `;
              })
              .join("")}
          </div>
        `;

        const unmuteButtons = Array.from(
          mutedEl.querySelectorAll(
            'button[data-error-action="unmute-muted-list"]',
          ),
        );
        unmuteButtons.forEach((button) => {
          button.addEventListener("click", async () => {
            const fingerprint = button.getAttribute("data-error-fingerprint");
            if (!fingerprint) return;
            await unmuteSignature(fingerprint);
          });
        });
      };

      const updateIssueCardState = (fingerprint, isMuted) => {
        const issueCards = Array.from(
          issuesEl.querySelectorAll(
            ".admin-errors-issue-card[data-error-fingerprint]",
          ),
        );
        const card = issueCards.find(
          (item) => item.getAttribute("data-error-fingerprint") === fingerprint,
        );
        if (!card) return;

        if (isMuted) {
          card.classList.add("is-muted");
        } else {
          card.classList.remove("is-muted");
        }

        const status = card.querySelector(".admin-errors-issue-status");
        if (status) {
          status.textContent = isMuted ? "Muted" : "Active";
        }

        const button = card.querySelector(
          'button[data-error-action="toggle-mute"]',
        );
        if (!button) return;
        button.textContent = isMuted ? "Unmute Signature" : "Mute Signature";
      };

      const syncSelectedEventActions = (fingerprint) => {
        const issue = issuesByFingerprint.get(fingerprint);
        if (!issue) return;

        const button = eventsEl.querySelector(
          'button[data-error-action="toggle-mute-selected"]',
        );
        if (!button) return;
        button.textContent = issue.isMuted
          ? "Unmute Signature"
          : "Mute Signature";
      };

      const muteSignature = async (fingerprint) => {
        try {
          messageEl.innerHTML =
            '<div class="message info">Muting this signature for future events...</div>';
          await this.fetchAdminMuteErrorSignature(fingerprint, selectedChannel);

          const issue = issuesByFingerprint.get(fingerprint);
          if (issue) {
            issue.isMuted = true;
          }

          const existing = mutedByFingerprint.get(fingerprint) || {};
          mutedByFingerprint.set(fingerprint, {
            fingerprint,
            mutedAt: Date.now(),
            mutedByEmail:
              this.currentUser?.email || existing.mutedByEmail || null,
            mutedByUserId:
              this.currentUser?.userId || existing.mutedByUserId || null,
            message: issue?.message || existing.message || "Unknown error",
            totalEvents: issue?.eventCount || existing.totalEvents || 0,
            lastSeen: issue?.lastSeen || existing.lastSeen || null,
          });

          updateIssueCardState(fingerprint, true);
          syncSelectedEventActions(fingerprint);
          renderMutedSignatures();

          messageEl.innerHTML =
            '<div class="message success">✓ Signature muted. Future events with this fingerprint will be ignored.</div>';
        } catch (error) {
          console.error("Muting error signature failed:", error);
          messageEl.innerHTML =
            '<div class="message error">✗ Failed to mute this signature.</div>';
        }
      };

      const unmuteSignature = async (fingerprint) => {
        try {
          messageEl.innerHTML =
            '<div class="message info">Removing mute for this signature...</div>';
          await this.fetchAdminUnmuteErrorSignature(
            fingerprint,
            selectedChannel,
          );

          const issue = issuesByFingerprint.get(fingerprint);
          if (issue) {
            issue.isMuted = false;
          }

          mutedByFingerprint.delete(fingerprint);
          updateIssueCardState(fingerprint, false);
          syncSelectedEventActions(fingerprint);
          renderMutedSignatures();

          messageEl.innerHTML =
            '<div class="message success">✓ Signature unmuted. Future events will be recorded again.</div>';
        } catch (error) {
          console.error("Unmuting error signature failed:", error);
          messageEl.innerHTML =
            '<div class="message error">✗ Failed to unmute this signature.</div>';
        }
      };

      const toggleMute = async (fingerprint) => {
        const issue = issuesByFingerprint.get(fingerprint);
        if (issue?.isMuted) {
          await unmuteSignature(fingerprint);
        } else {
          await muteSignature(fingerprint);
        }
      };

      renderMutedSignatures();

      if (visibleIssues.length === 0) {
        issuesEl.innerHTML =
          '<p style="color: var(--text-secondary);">No active (unmuted) errors recorded in the selected window.</p>';
        eventsEl.innerHTML =
          '<p style="color: var(--text-secondary);">No issue selected.</p>';
      } else {
        issuesEl.innerHTML = `
          <div class="admin-errors-issues-list">
            ${visibleIssues
              .map((issue) => {
                const users = issue.users?.length
                  ? issue.users.join(", ")
                  : "No authenticated users captured";
                const issueHref = `/admin/errors?fingerprint=${encodeURIComponent(issue.fingerprint)}`;
                const muteLabel = issue.isMuted
                  ? "Unmute Signature"
                  : "Mute Signature";
                return `
                  <div class="admin-errors-issue-card ${issue.isMuted ? "is-muted" : ""}" data-error-fingerprint="${this.escapeHtml(issue.fingerprint)}">
                    <div class="admin-errors-issue-message">${this.escapeHtml(issue.message)}</div>
                    <div class="admin-errors-issue-meta">
                      <span>Count: ${issue.eventCount}</span>
                      <span>Last seen: ${new Date(issue.lastSeen).toLocaleString()}</span>
                      <span>Status: <span class="admin-errors-issue-status">${issue.isMuted ? "Muted" : "Active"}</span></span>
                    </div>
                    <div class="admin-errors-issue-users"><strong>Users:</strong> ${this.escapeHtml(users)}</div>
                    <div class="admin-errors-issue-actions">
                      <a href="${issueHref}" class="button-base auth-button admin-errors-issue-action admin-errors-light-button" target="_blank" rel="noopener noreferrer">Open in New Tab</a>
                      <button type="button" class="admin-errors-mute-button" data-error-action="toggle-mute" data-error-fingerprint="${this.escapeHtml(issue.fingerprint)}">${muteLabel}</button>
                    </div>
                    <div class="admin-errors-issue-open">Open in a new tab to inspect full event data and generate a handoff payload.</div>
                  </div>
                `;
              })
              .join("")}
          </div>
        `;

        eventsEl.innerHTML = requestedFingerprint
          ? '<div class="message info">Loading full error events...</div>'
          : '<p style="color: var(--text-secondary);">Open an issue in a new tab to inspect full event data.</p>';

        const issueCards = Array.from(
          issuesEl.querySelectorAll(
            ".admin-errors-issue-card[data-error-fingerprint]",
          ),
        );

        const loadErrorEvents = async (fingerprint, cardElement) => {
          issueCards.forEach((card) => card.classList.remove("is-selected"));
          if (cardElement) {
            cardElement.classList.add("is-selected");
          }

          eventsEl.innerHTML =
            '<div class="message info">Loading full error events...</div>';

          try {
            const eventsResponse = await this.fetchAdminErrorEvents(
              fingerprint,
              60,
              200,
              selectedChannel,
            );
            const events = eventsResponse.events || [];
            const issue = issuesByFingerprint.get(fingerprint) || null;
            const isMuted = Boolean(issue?.isMuted || eventsResponse.isMuted);
            const handoffPayload = this.buildAdminErrorHandoff(
              issue,
              eventsResponse,
              selectedChannel,
            );

            if (!events.length) {
              eventsEl.innerHTML =
                '<p style="color: var(--text-secondary);">No events found for this issue in the selected window.</p>';
              return;
            }

            eventsEl.innerHTML = `
              <div class="admin-errors-events-header">
                <div><strong>Fingerprint:</strong> ${this.escapeHtml(eventsResponse.fingerprint || fingerprint)}</div>
                <div><strong>Channel:</strong> ${this.escapeHtml(eventsResponse.channel || selectedChannel)}</div>
                <div><strong>Showing:</strong> ${eventsResponse.returnedEvents} / ${eventsResponse.totalEvents} events</div>
                <div><strong>Status:</strong> ${isMuted ? "Muted" : "Active"}</div>
              </div>
              <div class="admin-errors-events-actions">
                <button type="button" class="button-base auth-button admin-errors-copy-button admin-errors-light-button" data-error-action="copy-handoff">Copy AI Handoff String</button>
                <button type="button" class="admin-errors-mute-button" data-error-action="toggle-mute-selected" data-error-fingerprint="${this.escapeHtml(fingerprint)}">${isMuted ? "Unmute Signature" : "Mute Signature"}</button>
              </div>
              <div class="admin-errors-events-list">
                ${events
                  .map((eventRow) => {
                    const payloadText = eventRow.payloadJson
                      ? this.escapeHtml(
                          JSON.stringify(
                            eventRow.payload || eventRow.payloadJson,
                            null,
                            2,
                          ),
                        )
                      : "null";

                    return `
                      <div class="admin-errors-event-card">
                        <div class="admin-errors-event-meta">
                          <span><strong>ID:</strong> ${this.escapeHtml(eventRow.id || "")}</span>
                          <span><strong>Created:</strong> ${eventRow.createdAt ? new Date(eventRow.createdAt).toLocaleString() : "Unknown"}</span>
                          <span><strong>User:</strong> ${this.escapeHtml(eventRow.userEmail || eventRow.userId || "Anonymous")}</span>
                        </div>
                        <div class="admin-errors-event-meta">
                          <span><strong>Source:</strong> ${this.escapeHtml(eventRow.source || "")}</span>
                          <span><strong>App:</strong> ${this.escapeHtml(eventRow.appVersion || "")}</span>
                          <span><strong>Build:</strong> ${this.escapeHtml(eventRow.buildHash || "")}</span>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Message</strong>
                          <pre>${this.escapeHtml(eventRow.errorMessage || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Stack</strong>
                          <pre>${this.escapeHtml(eventRow.errorStack || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>URL</strong>
                          <pre>${this.escapeHtml(eventRow.url || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>User Agent</strong>
                          <pre>${this.escapeHtml(eventRow.userAgent || "")}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Session / Assets</strong>
                          <pre>${this.escapeHtml(eventRow.sessionId || "")}${eventRow.assetsBytes ? `\nassets bytes: ${eventRow.assetsBytes}` : ""}</pre>
                        </div>
                        <div class="admin-errors-event-field">
                          <strong>Payload JSON</strong>
                          <pre>${payloadText}</pre>
                        </div>
                      </div>
                    `;
                  })
                  .join("")}
              </div>
            `;

            const copyButton = eventsEl.querySelector(
              'button[data-error-action="copy-handoff"]',
            );
            if (copyButton) {
              copyButton.addEventListener("click", async () => {
                try {
                  await this.copyTextToClipboard(handoffPayload);
                  const originalText = copyButton.textContent;
                  copyButton.textContent = "Copied";
                  setTimeout(() => {
                    copyButton.textContent = originalText;
                  }, 1200);
                } catch (error) {
                  console.error("Copy handoff failed:", error);
                  messageEl.innerHTML =
                    '<div class="message error">✗ Failed to copy handoff string.</div>';
                }
              });
            }

            const muteButton = eventsEl.querySelector(
              'button[data-error-action="toggle-mute-selected"]',
            );
            if (muteButton) {
              muteButton.addEventListener("click", async () => {
                await toggleMute(fingerprint);
              });
            }
          } catch (error) {
            console.error("Loading error events failed:", error);
            eventsEl.innerHTML =
              '<div class="message error">✗ Failed to load selected issue events.</div>';
          }
        };

        issueCards.forEach((card) => {
          const muteButton = card.querySelector(
            'button[data-error-action="toggle-mute"]',
          );
          if (!muteButton) return;

          muteButton.addEventListener("click", async (event) => {
            event.preventDefault();
            event.stopPropagation();
            const fingerprint = muteButton.getAttribute(
              "data-error-fingerprint",
            );
            if (!fingerprint) {
              return;
            }

            await toggleMute(fingerprint);
          });
        });

        if (requestedFingerprint) {
          const requestedCard = issueCards.find(
            (card) =>
              card.getAttribute("data-error-fingerprint") ===
              requestedFingerprint,
          );

          if (requestedCard) {
            await loadErrorEvents(requestedFingerprint, requestedCard);
          } else if (issuesByFingerprint.has(requestedFingerprint)) {
            await loadErrorEvents(requestedFingerprint, null);
          } else {
            eventsEl.innerHTML =
              '<p style="color: var(--text-secondary);">Requested issue was not found in this time window.</p>';
          }
        }
      }

      messageEl.innerHTML = "";
    } catch (error) {
      console.error("Admin error details failed:", error);
      messageEl.innerHTML =
        '<div class="message error">✗ Failed to load error details. Please try again.</div>';
    }
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  formatOwnershipTransferPolicy(policy) {
    return policy === "remove"
      ? "Removed entirely"
      : `Kept as ${this.escapeHtml(policy)}`;
  }

  getCurrentPathWithSearch() {
    return `${window.location.pathname}${window.location.search}`;
  }

  renderInvitePageState({
    messageMarkup = "",
    detailsMarkup = "",
    actionsMarkup = "",
    leadText = "Review this invitation and accept it when you are ready.",
  }) {
    const lead = document.getElementById("inviteLead");
    const messageContainer = document.getElementById("inviteMessageContainer");
    const details = document.getElementById("inviteDetails");
    const actions = document.getElementById("inviteActions");

    if (lead) {
      lead.textContent = leadText;
    }
    if (messageContainer) {
      messageContainer.innerHTML = messageMarkup;
    }
    if (details) {
      details.innerHTML = detailsMarkup;
    }
    if (actions) {
      actions.innerHTML = actionsMarkup;
    }
  }

  renderTransferPageState({
    messageMarkup = "",
    detailsMarkup = "",
    actionsMarkup = "",
    leadText = "Review this ownership transfer and accept it when you are ready.",
  }) {
    const lead = document.getElementById("transferLead");
    const messageContainer = document.getElementById(
      "transferMessageContainer",
    );
    const details = document.getElementById("transferDetails");
    const actions = document.getElementById("transferActions");

    if (lead) {
      lead.textContent = leadText;
    }
    if (messageContainer) {
      messageContainer.innerHTML = messageMarkup;
    }
    if (details) {
      details.innerHTML = detailsMarkup;
    }
    if (actions) {
      actions.innerHTML = actionsMarkup;
    }
  }

  async fetchInvitationStatus(token) {
    const response = await fetch(
      `/api/cloud/invitations/accept?token=${encodeURIComponent(token)}`,
      {
        credentials: "include",
      },
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to load invitation");
    }
    return data;
  }

  async acceptInvitation(token) {
    const response = await fetch("/api/cloud/invitations/accept", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to accept invitation");
    }
    return data;
  }

  async declineInvitation(token) {
    const response = await fetch("/api/cloud/invitations/decline", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to decline invitation");
    }
    return data;
  }

  async fetchTransferStatus(token) {
    const response = await fetch(
      `/api/cloud/ownership-transfers/accept?token=${encodeURIComponent(token)}`,
      {
        credentials: "include",
      },
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to load ownership transfer");
    }
    return data;
  }

  async acceptOwnershipTransfer(token) {
    const response = await fetch("/api/cloud/ownership-transfers/accept", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to accept ownership transfer");
    }
    return data;
  }

  async declineOwnershipTransfer(token) {
    const response = await fetch("/api/cloud/ownership-transfers/decline", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "Failed to decline ownership transfer");
    }
    return data;
  }

  initializeInvitePage() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      this.renderInvitePageState({
        messageMarkup:
          '<div class="message error">✗ This invitation link is missing its token.</div>',
        actionsMarkup:
          '<a href="/account" class="button-base auth-button">Go to account</a>',
      });
      return;
    }

    this.renderInvitePageState({
      messageMarkup:
        '<div class="message info">Loading invitation details...</div>',
    });

    void this.fetchInvitationStatus(token)
      .then((data) => {
        const invitation = data.invitation;
        const invitationDetails = `
          <div class="account-card">
            <p><strong>Font:</strong> ${this.escapeHtml(invitation.assetName)}</p>
            <p><strong>Access:</strong> ${this.escapeHtml(invitation.role)}</p>
            <p><strong>Invited email:</strong> ${this.escapeHtml(invitation.email)}</p>
            ${
              invitation.invitedByEmail
                ? `<p><strong>Invited by:</strong> ${this.escapeHtml(invitation.invitedByEmail)}</p>`
                : ""
            }
          </div>
        `;

        if (data.acceptedByCurrentUser) {
          this.renderInvitePageState({
            messageMarkup:
              '<div class="message success">✓ This invitation has already been accepted for your account.</div>',
            detailsMarkup: invitationDetails,
            actionsMarkup: `
              <a href="${this.escapeHtml(data.editorUrl)}" class="button-base auth-button">Open in editor</a>
              <a href="/account" class="button-base subscription-button">Maybe later</a>
            `,
            leadText: "You can open the shared font whenever you want.",
          });
          return;
        }

        if (data.requiresLogin) {
          this.renderInvitePageState({
            messageMarkup:
              '<div class="message info">Sign in with the invited email address to accept this invitation.</div>',
            detailsMarkup: invitationDetails,
            actionsMarkup:
              '<button id="inviteSignInButton" class="button-base auth-button">Sign in to accept</button>',
            leadText:
              "You do not need to open the asset immediately after accepting.",
          });

          document
            .getElementById("inviteSignInButton")
            ?.addEventListener("click", () => {
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        if (data.emailMatches === false) {
          this.renderInvitePageState({
            messageMarkup: `<div class="message error">✗ You are signed in as ${this.escapeHtml(
              data.currentUserEmail,
            )}, but this invitation was sent to ${this.escapeHtml(invitation.email)}.</div>`,
            detailsMarkup: invitationDetails,
            actionsMarkup:
              '<button id="inviteSwitchAccountButton" class="button-base auth-button">Use a different account</button>',
          });

          document
            .getElementById("inviteSwitchAccountButton")
            ?.addEventListener("click", () => {
              this.logout();
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        this.renderInvitePageState({
          messageMarkup:
            '<div class="message info">Accept this invitation to add the shared font to your cloud files.</div>',
          detailsMarkup: invitationDetails,
          actionsMarkup: `
            <button id="inviteAcceptButton" class="button-base auth-button">Accept invitation</button>
            <button id="inviteDeclineButton" class="button-base secondary-button">Decline invitation</button>
          `,
          leadText:
            "After you accept, you will stay on this page and get a button to open the font in the editor.",
        });

        document
          .getElementById("inviteAcceptButton")
          ?.addEventListener("click", async () => {
            const button = document.getElementById("inviteAcceptButton");
            if (button) {
              button.setAttribute("disabled", "disabled");
              button.textContent = "Accepting...";
            }

            try {
              const accepted = await this.acceptInvitation(token);
              this.renderInvitePageState({
                messageMarkup: `<div class="message success">✓ You now have ${this.escapeHtml(
                  accepted.role,
                )} access to ${this.escapeHtml(accepted.assetName)}.</div>`,
                detailsMarkup: invitationDetails,
                actionsMarkup: `
                  <a href="${this.escapeHtml(accepted.editorUrl)}" class="button-base auth-button">Open in editor</a>
                  <a href="/account" class="button-base subscription-button">Maybe later</a>
                `,
                leadText:
                  "The invitation is accepted. You can open the shared font now or come back later.",
              });
            } catch (error) {
              this.renderInvitePageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: invitationDetails,
                actionsMarkup:
                  '<button id="inviteRetryButton" class="button-base auth-button">Try again</button>',
              });
              document
                .getElementById("inviteRetryButton")
                ?.addEventListener("click", () => this.initializeInvitePage());
            }
          });

        document
          .getElementById("inviteDeclineButton")
          ?.addEventListener("click", async () => {
            const declineButton = document.getElementById(
              "inviteDeclineButton",
            );
            const acceptButton = document.getElementById("inviteAcceptButton");
            if (declineButton) {
              declineButton.setAttribute("disabled", "disabled");
              declineButton.textContent = "Declining...";
            }
            if (acceptButton) {
              acceptButton.setAttribute("disabled", "disabled");
            }

            try {
              await this.declineInvitation(token);
              this.renderInvitePageState({
                messageMarkup:
                  '<div class="message success">✓ Invitation declined.</div>',
                detailsMarkup: invitationDetails,
                actionsMarkup:
                  '<a href="/account" class="button-base auth-button">Go to account</a>',
                leadText:
                  "This invitation has been declined. You can return to your account at any time.",
              });
            } catch (error) {
              this.renderInvitePageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: invitationDetails,
                actionsMarkup: `
                  <button id="inviteAcceptButton" class="button-base auth-button">Accept invitation</button>
                  <button id="inviteDeclineButton" class="button-base secondary-button">Decline invitation</button>
                `,
              });
              this.initializeInvitePage();
            }
          });
      })
      .catch((error) => {
        this.renderInvitePageState({
          messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          actionsMarkup:
            '<a href="/account" class="button-base auth-button">Go to account</a>',
        });
      });
  }

  initializeTransferPage() {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");

    if (!token) {
      this.renderTransferPageState({
        messageMarkup:
          '<div class="message error">✗ This ownership transfer link is missing its token.</div>',
        actionsMarkup:
          '<a href="/account" class="button-base auth-button">Go to account</a>',
      });
      return;
    }

    this.renderTransferPageState({
      messageMarkup:
        '<div class="message info">Loading ownership transfer details...</div>',
    });

    void this.fetchTransferStatus(token)
      .then((data) => {
        const transfer = data.transfer;
        const transferDetails = `
          <div class="account-card">
            <p><strong>Font:</strong> ${this.escapeHtml(transfer.assetName)}</p>
            <p><strong>New owner email:</strong> ${this.escapeHtml(transfer.targetEmail)}</p>
            <p><strong>Current owner:</strong> ${this.escapeHtml(transfer.sourceOwnerEmail)}</p>
            <p><strong>Current owner after transfer:</strong> ${this.formatOwnershipTransferPolicy(transfer.previousOwnerRole)}</p>
          </div>
        `;

        if (data.acceptedByCurrentUser) {
          this.renderTransferPageState({
            messageMarkup:
              '<div class="message success">✓ This ownership transfer has already been accepted for your account.</div>',
            detailsMarkup: transferDetails,
            actionsMarkup: `
              <a href="${this.escapeHtml(data.editorUrl)}" class="button-base auth-button">Open in editor</a>
              <a href="/account" class="button-base subscription-button">Maybe later</a>
            `,
            leadText: "You can open the transferred font whenever you want.",
          });
          return;
        }

        if (transfer.acceptedAt) {
          this.renderTransferPageState({
            messageMarkup:
              '<div class="message success">✓ This ownership transfer has already been completed.</div>',
            detailsMarkup: transferDetails,
            actionsMarkup:
              '<a href="/account" class="button-base auth-button">Go to account</a>',
            leadText: "This transfer is no longer pending.",
          });
          return;
        }

        if (data.requiresLogin) {
          this.renderTransferPageState({
            messageMarkup:
              '<div class="message info">Sign in with the target email address to accept this ownership transfer.</div>',
            detailsMarkup: transferDetails,
            actionsMarkup:
              '<button id="transferSignInButton" class="button-base auth-button">Sign in to review</button>',
            leadText:
              "You do not need to open the asset immediately after accepting ownership.",
          });

          document
            .getElementById("transferSignInButton")
            ?.addEventListener("click", () => {
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        if (data.emailMatches === false) {
          this.renderTransferPageState({
            messageMarkup: `<div class="message error">✗ You are signed in as ${this.escapeHtml(
              data.currentUserEmail,
            )}, but this ownership transfer was sent to ${this.escapeHtml(transfer.targetEmail)}.</div>`,
            detailsMarkup: transferDetails,
            actionsMarkup:
              '<button id="transferSwitchAccountButton" class="button-base auth-button">Use a different account</button>',
          });

          document
            .getElementById("transferSwitchAccountButton")
            ?.addEventListener("click", () => {
              this.logout();
              this.redirectToLoginPath(this.getCurrentPathWithSearch());
            });
          return;
        }

        this.renderTransferPageState({
          messageMarkup:
            '<div class="message info">Accept this transfer to become the new owner of the shared font.</div>',
          detailsMarkup: transferDetails,
          actionsMarkup: `
            <button id="transferAcceptButton" class="button-base auth-button">Accept transfer</button>
            <button id="transferDeclineButton" class="button-base secondary-button">Decline transfer</button>
          `,
          leadText:
            "After you accept, you will stay on this page and get a button to open the font in the editor.",
        });

        document
          .getElementById("transferAcceptButton")
          ?.addEventListener("click", async () => {
            const button = document.getElementById("transferAcceptButton");
            if (button) {
              button.setAttribute("disabled", "disabled");
              button.textContent = "Accepting...";
            }

            try {
              const accepted = await this.acceptOwnershipTransfer(token);
              this.renderTransferPageState({
                messageMarkup: `<div class="message success">✓ You are now the owner of ${this.escapeHtml(
                  accepted.assetName,
                )}.</div>`,
                detailsMarkup: transferDetails,
                actionsMarkup: `
                  <a href="${this.escapeHtml(accepted.editorUrl)}" class="button-base auth-button">Open in editor</a>
                  <a href="/account" class="button-base subscription-button">Maybe later</a>
                `,
                leadText:
                  "The ownership transfer is complete. You can open the font now or come back later.",
              });
            } catch (error) {
              this.renderTransferPageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: transferDetails,
                actionsMarkup:
                  '<button id="transferRetryButton" class="button-base auth-button">Try again</button>',
              });
              document
                .getElementById("transferRetryButton")
                ?.addEventListener("click", () =>
                  this.initializeTransferPage(),
                );
            }
          });

        document
          .getElementById("transferDeclineButton")
          ?.addEventListener("click", async () => {
            const declineButton = document.getElementById(
              "transferDeclineButton",
            );
            const acceptButton = document.getElementById(
              "transferAcceptButton",
            );
            if (declineButton) {
              declineButton.setAttribute("disabled", "disabled");
              declineButton.textContent = "Declining...";
            }
            if (acceptButton) {
              acceptButton.setAttribute("disabled", "disabled");
            }

            try {
              await this.declineOwnershipTransfer(token);
              this.renderTransferPageState({
                messageMarkup:
                  '<div class="message success">✓ Ownership transfer declined.</div>',
                detailsMarkup: transferDetails,
                actionsMarkup:
                  '<a href="/account" class="button-base auth-button">Go to account</a>',
                leadText:
                  "This ownership transfer has been declined. You can return to your account at any time.",
              });
            } catch (error) {
              this.renderTransferPageState({
                messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
                detailsMarkup: transferDetails,
                actionsMarkup: `
                  <button id="transferAcceptButton" class="button-base auth-button">Accept transfer</button>
                  <button id="transferDeclineButton" class="button-base secondary-button">Decline transfer</button>
                `,
              });
              this.initializeTransferPage();
            }
          });
      })
      .catch((error) => {
        this.renderTransferPageState({
          messageMarkup: `<div class="message error">✗ ${this.escapeHtml(error.message)}</div>`,
          actionsMarkup:
            '<a href="/account" class="button-base auth-button">Go to account</a>',
        });
      });
  }

  /**
   * Initialize login form handler
   */
  initializeLoginForm() {
    const loginForm = document.getElementById("loginForm");
    const messageContainer = document.getElementById("messageContainer");

    if (!loginForm) return;

    loginForm.addEventListener("submit", async (e) => {
      e.preventDefault();

      const email = document.getElementById("email").value;
      const submitBtn = document.getElementById("submitBtn");

      submitBtn.disabled = true;
      submitBtn.textContent = "Sending...";

      // Capture returnTo from URL if present
      const params = new URLSearchParams(window.location.search);
      const returnTo = params.get("returnTo") || null;

      try {
        const response = await fetch("/api/auth/request-login", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            returnTo,
            trackingParams: this.getTrackingParams(),
          }),
        });

        const data = await response.json();

        if (response.ok) {
          messageContainer.innerHTML = `<div class="message success">✓ Magic link sent! Check your email to sign in, and don't forget to check your spam folder.</div>`;
          loginForm.reset();

          // Track signup/login request event
          this.trackEvent(
            data.isNewUser ? "signup-initiated" : "login-initiated",
            { method: "magic-link" },
          );
        } else {
          messageContainer.innerHTML = `<div class="message error">✗ ${
            data.error || "Failed to send magic link"
          }</div>`;
        }
      } catch (error) {
        console.error("Login form error:", error);
        messageContainer.innerHTML = `<div class="message error">✗ Network error. Please try again.</div>`;
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = "Send Magic Link";
      }
    });

    // Check for error messages in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get("error")) {
      const errorMessages = {
        invalid_token: "Invalid magic link. Please request a new one.",
        expired_token: "This magic link has expired. Please request a new one.",
        user_not_found: "User not found. Please try again.",
        verification_failed: "Verification failed. Please try again.",
      };
      messageContainer.innerHTML = `<div class="message error">✗ ${
        errorMessages[params.get("error")] || "An error occurred"
      }</div>`;
    }
  }

  /**
   * Initialize account page
   */
  initializeAccountPage() {
    const accountInfo = document.getElementById("accountInfo");
    const emailChangeSection = document.getElementById("emailChangeSection");
    if (!accountInfo) return;

    const user = this.getSessionUser();
    if (!user) {
      // Not logged in, redirect to login
      this.loadPage("/login", true);
      return;
    }

    // Display account information
    const createdDate = user.createdAt
      ? new Date(user.createdAt).toLocaleDateString()
      : "Unknown";
    accountInfo.innerHTML = `
      <p><span style="color: var(--text-secondary); font-weight: 500;">Email:</span> ${user.email}</p>
      <p><span style="color: var(--text-secondary); font-weight: 500;">User ID:</span> <code style="font-family: var(--font-families-mono); font-size: 13px; color: var(--text-muted);">${user.userId}</code></p>
      <p><span style="color: var(--text-secondary); font-weight: 500;">Member since:</span> ${createdDate}</p>
    `;

    // Initialize subscription section
    this.accountMembership = null;
    this.initializeSubscriptionSection();

    // Show email change section
    if (emailChangeSection) {
      emailChangeSection.classList.remove("hidden");
      this.initializeEmailChangeForm();
    }

    // Check for success/error messages in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscription") === "success") {
      this.showAuthSuccess(
        "Subscription checkout completed! Your account is being set up.",
      );
      window.history.replaceState({}, "", window.location.pathname);

      // Reload subscription info after a short delay to allow webhook to process
      setTimeout(() => {
        this.initializeSubscriptionSection();
      }, 2000);
    } else if (params.get("subscription") === "canceled") {
      this.showAuthError("Subscription signup was canceled.");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("email_changed") === "success") {
      this.showAuthSuccess("Your email address has been updated successfully!");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("error")) {
      this.showAuthError(params.get("error"));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }

  /**
   * Initialize subscription section
   */
  async initializeSubscriptionSection() {
    const subscriptionInfo = document.getElementById("subscriptionInfo");
    const creditsSection = document.getElementById("creditsSection");
    const creditsInfo = document.getElementById("creditsInfo");

    if (!subscriptionInfo) return;

    try {
      console.log("Fetching subscription status...");
      const response = await fetch("/api/subscription/status");

      if (!response.ok) {
        throw new Error("Failed to load subscription status");
      }

      const data = await response.json();
      console.log("Subscription data:", data);
      this.accountMembership = data.membership || data.subscription || null;

      // Render subscription status
      if (data.hasSubscription) {
        const sub = data.subscription;
        const isSponsored = !!sub.isSponsored;
        const statusBadge = isSponsored
          ? '<span class="subscription-trial-badge">Sponsored</span>'
          : "";
        const periodEnd = sub.currentPeriodEnd
          ? new Date(sub.currentPeriodEnd * 1000).toLocaleDateString()
          : null;

        let billingRows = "";
        if (sub.showPriceDetails && sub.priceCents != null && sub.countryCode) {
          const vatRate = EU_VAT_RATES[sub.countryCode];
          const isEU = vatRate !== undefined;
          const priceWithVat = isEU
            ? ((sub.priceCents * (1 + vatRate)) / 100).toFixed(2)
            : (sub.priceCents / 100).toFixed(2);
          const vatBadge = isEU
            ? ` <span style="font-size: 12px; color: var(--text-muted); font-weight: 400;">incl. ${(
                vatRate * 100
              ).toFixed(0)}% VAT</span>`
            : "";

          billingRows = `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Price</span>
              <span class="subscription-plan-value">€${priceWithVat}/${
                sub.billingPeriod === "yearly" ? "year" : "month"
              }${vatBadge}</span>
            </div>
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Next billing date</span>
              <span class="subscription-plan-value">${periodEnd}</span>
            </div>
          `;
        } else if (periodEnd) {
          billingRows = `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Access until</span>
              <span class="subscription-plan-value">${periodEnd}</span>
            </div>
          `;
        }

        const actionsMarkup = sub.showBillingControls
          ? `
            <div class="subscription-actions">
              <button class="button-base subscription-button" onclick="window.appNavigator.openCustomerPortal()" style="display: none;">Manage Subscription</button>
              ${
                sub.cancelAtPeriodEnd
                  ? '<button class="button-base subscription-button" onclick="window.appNavigator.reactivateSubscription()">Reactivate Subscription</button>'
                  : '<button class="button-base subscription-button danger" onclick="window.appNavigator.cancelSubscription()">Cancel Subscription</button>'
              }
            </div>
          `
          : '<p style="color: var(--text-secondary); margin: 16px 0 0 0;">This membership is managed internally.</p>';

        subscriptionInfo.innerHTML = `
          <div class="subscription-plan-info">
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Plan</span>
              <span class="subscription-plan-value">${this.escapeHtml(sub.planName || "Membership")}${
                sub.billingPeriod
                  ? `(${sub.billingPeriod === "yearly" ? "Yearly" : "Monthly"})`
                  : ""
              }${statusBadge}</span>
            </div>
            ${billingRows}
            ${
              sub.cancelAtPeriodEnd && sub.showBillingControls
                ? `
            <div class="subscription-plan-row">
              <span class="subscription-plan-label">Status</span>
              <span class="subscription-plan-value" style="color: #ff4444;">Cancels on ${periodEnd}</span>
            </div>
            `
                : ""
            }
          </div>
          ${actionsMarkup}
        `;

        // Show and populate credits section
        if (creditsSection && creditsInfo) {
          creditsSection.classList.remove("hidden");

          if (sub.isSponsored || data.credits.isUnlimited) {
            creditsInfo.innerHTML = `
              <div class="credits-balance">
                <span class="credits-amount">Included</span>
                <span class="credits-label">AI agent usage is covered by Counterpunch</span>
              </div>
            `;
          }
          // Check if subscription is cancelled
          else if (sub.cancelAtPeriodEnd) {
            creditsInfo.innerHTML = `
              <div class="credits-balance">
                <span class="credits-label" style="color: var(--text-muted);">Credits require an active subscription</span>
              </div>
            `;
          } else {
            // Determine if showing credits or charges
            const isNegative = data.credits.balanceCents < 0;
            const displayAmount = isNegative
              ? data.credits.balance.replace("-", "")
              : data.credits.balance;
            const displayLabel = isNegative
              ? "pay-as-you-go usage charges this period"
              : "available credits";

            creditsInfo.innerHTML = `
              <div class="credits-balance">
                <span class="credits-amount" style="${
                  isNegative ? "color: #ff9800;" : ""
                }">${displayAmount}</span>
                <span class="credits-label">${displayLabel}</span>
              </div>
              
              ${
                data.priceChangeNotifications &&
                data.priceChangeNotifications.length > 0
                  ? `
                ${data.priceChangeNotifications
                  .map(
                    (pc) => `
                  <div class="price-change-notification">
                    <div class="price-change-notification-title">Price Change Notice</div>
                    <div class="price-change-notification-details">
                      Your subscription price will change from ${
                        pc.oldPrice
                      } to ${pc.newPrice} 
                      on ${new Date(
                        pc.effectiveDate * 1000,
                      ).toLocaleDateString()}.
                    </div>
                    <button class="price-change-notification-dismiss" onclick="window.appNavigator.dismissPriceChange('${
                      pc.id
                    }')">
                      Dismiss
                    </button>
                  </div>
                `,
                  )
                  .join("")}
              `
                  : ""
              }
              
              <div class="credits-overage-setting">
                <label>
                  <input 
                    type="checkbox" 
                    id="overageAllowed" 
                    ${data.credits.overageAllowed ? "checked" : ""}
                    onchange="window.appNavigator.updateOverageSetting(this.checked)"
                  />
                  <span>Enable pay-as-you-go when credits run out</span>
                </label>
                <div class="credits-overage-help">
                  When enabled, you'll be charged for usage that exceeds your credits. Otherwise, AI agent features are disabled when credits reach €0 until the credits reset on the next billing date.
                </div>
              </div>
            `;
          }
        }
      } else {
        this.accountMembership = null;
        if (creditsSection && creditsInfo) {
          creditsSection.classList.remove("hidden");
          creditsInfo.innerHTML = `
            <div class="credits-balance">
              <span class="credits-amount">${data.credits.balance}</span>
              <span class="credits-label">free monthly AI agent credit</span>
            </div>
          `;
        }
        // No subscription - show link to pricing page
        subscriptionInfo.innerHTML = `
          <p style="color: var(--text-secondary); margin-bottom: 16px;">
            You don't have an active subscription.
          </p>
          <a href="/pricing" class="button-base subscription-button">View Pricing Plans</a>
        `;
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      this.accountMembership = null;
      subscriptionInfo.innerHTML = `
        <p style="color: #ff4444;">Failed to load subscription information. Please try again.</p>
      `;
    }
  }

  /**
   * Get pricing product configuration
   */
  getPricingProducts() {
    return getPricingProducts(); // Use exported function
  }

  /**
   * Generate pricing card HTML
   */
  generatePricingCardHTML(
    productId,
    elementIdPrefix = null,
    hasActiveSubscription = false,
  ) {
    const products = this.getPricingProducts();
    const product = products[productId];

    if (!product) {
      console.error(`Unknown product: ${productId}`);
      return "";
    }

    // Default to productId if no prefix specified
    const prefix = elementIdPrefix !== null ? elementIdPrefix : productId;

    const priceId = prefix + "Price";
    const periodId = prefix + "Period";
    const creditAmountId = prefix + "CreditAmount";
    const monthlyPrice = product.pricing.monthly.base.toFixed(2);
    const monthlyCredit = product.pricing.monthly.credit.toFixed(2);

    // Determine button behavior based on login status
    const isLoggedIn = this.isAuthenticated();
    const buttonOnClick = isLoggedIn
      ? `window.appNavigator.startSubscription()`
      : `window.appNavigator.redirectToLogin()`;

    // Hide button if user has active subscription
    const buttonHTML = hasActiveSubscription
      ? ""
      : `
          <button 
            class="pricing-button ${product.buttonClass}" 
            id="startSubscriptionBtn"
            onclick="${buttonOnClick}"
          >
            ${product.buttonText}
          </button>`;

    return `
      <div class="pricing-card pricing-card-paid">
        <div class="pricing-badge">${product.badge}</div>
        
        <div class="pricing-card-header">
          <h2>${product.name}</h2>
          <div class="pricing-price">
            <span class="pricing-amount" id="${priceId}">€${monthlyPrice}</span>
            <span class="pricing-period" id="${periodId}">per month</span>
            <span class="pricing-vat-badge" id="${priceId}_vat" style="display: none; margin-left: 8px; font-size: 12px; color: var(--text-muted); font-weight: 400;">excl. VAT</span>
          </div>
        </div>
        
        <div class="pricing-card-content">
          <ul class="pricing-features">
            ${product.features
              .map((feature) => {
                if (feature.dynamic) {
                  return `
                    <li>
                      <span class="material-symbols-outlined pricing-${
                        feature.icon === "check_circle" ? "check" : "x"
                      }">${feature.icon}</span>
                      <span id="${creditAmountId}">€${monthlyCredit} ${
                        feature.text
                      } per month</span>
                    </li>
                  `;
                } else {
                  return `
                    <li>
                      <span class="material-symbols-outlined pricing-${
                        feature.icon === "check_circle" ? "check" : "x"
                      }">${feature.icon}</span>
                      <span>${feature.text}</span>
                    </li>
                  `;
                }
              })
              .join("")}
          </ul>
          ${buttonHTML}
        </div>
      </div>
    `;
  }

  /**
   * Start subscription checkout
   */
  async startSubscription() {
    const btn = document.getElementById("startSubscriptionBtn");
    if (!btn) return;

    const toggle = document.getElementById("billingToggle");
    const billingPeriod = toggle?.checked ? "yearly" : "monthly";

    const countrySelect = document.getElementById("countrySelect");
    const countryCode = countrySelect?.value;

    if (!countryCode || countryCode === "loading") {
      this.showAuthError("Please select your country");
      return;
    }

    btn.disabled = true;
    btn.textContent = "Creating checkout session...";

    try {
      const response = await fetch("/api/subscription/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ billingPeriod, countryCode }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      this.showAuthError(error.message);
      btn.disabled = false;
      btn.textContent = "Subscribe";
    }
  }

  /**
   * Open Stripe Customer Portal
   */
  async openCustomerPortal() {
    try {
      const response = await fetch("/api/subscription/portal", {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to open customer portal");
      }
    } catch (error) {
      console.error("Portal error:", error);
      this.showAuthError(error.message);
    }
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription() {
    if (
      !confirm(
        "Are you sure you want to cancel your subscription? You'll still have access until the end of your billing period.",
      )
    ) {
      return;
    }

    try {
      const response = await fetch("/api/subscription/cancel", {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok) {
        this.showAuthSuccess(data.message);
        // Reload subscription info
        this.initializeSubscriptionSection();
      } else {
        throw new Error(data.error || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Cancel error:", error);
      this.showAuthError(error.message);
    }
  }

  /**
   * Reactivate cancelled subscription
   */
  async reactivateSubscription() {
    try {
      const response = await fetch("/api/subscription/reactivate", {
        method: "POST",
      });

      const data = await response.json();

      if (response.ok) {
        this.showAuthSuccess(data.message);
        // Reload subscription info
        this.initializeSubscriptionSection();
      } else {
        throw new Error(data.error || "Failed to reactivate subscription");
      }
    } catch (error) {
      console.error("Reactivate error:", error);
      this.showAuthError(error.message);
    }
  }

  /**
   * Update overage setting
   */
  async updateOverageSetting(allowed) {
    try {
      const response = await fetch("/api/subscription/update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ overageAllowed: allowed }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update setting");
      }

      // Show brief confirmation
      const label = document.querySelector(".credits-overage-setting label");
      const originalText = label.querySelector("span").textContent;
      label.querySelector("span").textContent = allowed
        ? "Pay-as-you-go enabled ✓"
        : "Pay-as-you-go disabled ✓";
      setTimeout(() => {
        label.querySelector("span").textContent = originalText;
      }, 2000);
    } catch (error) {
      console.error("Update setting error:", error);
      // Revert checkbox
      document.getElementById("overageAllowed").checked = !allowed;
      this.showAuthError("Failed to update setting");
    }
  }

  /**
   * Dismiss price change notification
   */
  async dismissPriceChange(notificationId) {
    // TODO: Implement API endpoint to acknowledge notification
    console.log("Dismiss notification:", notificationId);
  }

  /**
   * Initialize email change form handler
   */
  initializeEmailChangeForm() {
    const emailChangeForm = document.getElementById("emailChangeForm");
    const messageContainer = document.getElementById("emailChangeMessage");

    if (!emailChangeForm) return;

    emailChangeForm.addEventListener("submit", async (e) => {
      e.preventDefault();

      const newEmail = document.getElementById("newEmail").value;
      const submitBtn = emailChangeForm.querySelector('button[type="submit"]');

      submitBtn.disabled = true;
      submitBtn.textContent = "Sending...";
      messageContainer.style.display = "none";

      try {
        const response = await fetch("/api/auth/request-email-change", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ newEmail }),
        });

        const data = await response.json();

        if (response.ok) {
          messageContainer.innerHTML = `✓ ${data.message}`;
          messageContainer.className = "message success";
          messageContainer.style.display = "block";
          emailChangeForm.reset();
        } else {
          messageContainer.innerHTML = `✗ ${
            data.error || "Failed to send confirmation email"
          }`;
          messageContainer.className = "message error";
          messageContainer.style.display = "block";
        }
      } catch (error) {
        console.error("Email change form error:", error);
        messageContainer.innerHTML = `✗ Network error. Please try again.`;
        messageContainer.className = "message error";
        messageContainer.style.display = "block";
      } finally {
        submitBtn.disabled = false;
        submitBtn.textContent = "Send Confirmation Email";
      }
    });
  }

  /**
   * Load editor version info from GitHub for the home page
   */
  async loadEditorVersions() {
    try {
      const [latestRes, listRes] = await Promise.all([
        fetch('https://api.github.com/repos/counterpunchspace/editor/releases/latest'),
        fetch('https://api.github.com/repos/counterpunchspace/editor/releases?per_page=10')
      ]);
      const latest = await latestRes.json();
      const releases = await listRes.json();
      const prerelease = releases.find(r => r.prerelease);
      const midnight = (ts) => { const d = new Date(ts); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); };
      const daysAgo = (d) => Math.floor((midnight(Date.now()) - midnight(d)) / 86400000);
      const ageLabel = (n) => n === 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`;
      const stableLabel = document.getElementById('stableReleaseLabel');
      if (stableLabel && latest.tag_name) {
        const d = daysAgo(latest.published_at);
        stableLabel.innerHTML = `v${latest.tag_name.replace(/^v/, '')}  ·  <span class="material-symbols-outlined" style="font-size:12px;vertical-align:middle">calendar_month</span> ${ageLabel(d)}  ·  <a href="https://github.com/counterpunchspace/editor/releases/tag/${latest.tag_name}" target="_blank" rel="noopener noreferrer">changelog</a>`;
      }
      const previewLabel = document.getElementById('previewReleaseLabel');
      if (previewLabel && prerelease) {
        const d = daysAgo(prerelease.published_at);
        previewLabel.innerHTML = `v${prerelease.tag_name.replace(/^v/, '')}  ·  <span class="material-symbols-outlined" style="font-size:12px;vertical-align:middle">calendar_month</span> ${ageLabel(d)}  ·  <a href="https://github.com/counterpunchspace/editor/releases/tag/${prerelease.tag_name}" target="_blank" rel="noopener noreferrer">changelog</a>`;
      }
    } catch (e) {
      console.error('Failed to load editor versions:', e);
    }
  }

  /**
   * Initialize pricing page toggle
   */
  async initializePricingPage() {
    // Check subscription status first
    const isLoggedIn = this.isAuthenticated();
    let currentPricingPlan = isLoggedIn ? "free-trial" : "free";
    let hasActiveSubscription = false;
    let currentSubscription = null;
    if (isLoggedIn) {
      try {
        const response = await fetch("/api/subscription/status");
        if (response.ok) {
          const data = await response.json();
          currentSubscription = data.membership || data.subscription || null;
          hasActiveSubscription =
            data.hasSubscription &&
            (currentSubscription?.status === "active" ||
              currentSubscription?.status === "trialing");
          if (hasActiveSubscription && currentSubscription?.productId) {
            currentPricingPlan = currentSubscription.productId;
          }
        }
      } catch (error) {
        console.error("Failed to fetch subscription status:", error);
      }
    }

    this.updatePricingSubscriptionState({
      isLoggedIn,
      currentPricingPlan,
      hasActiveSubscription,
      currentSubscription,
    });

    // Update button based on auth and subscription status
    const btn = document.getElementById("startSubscriptionBtn");
    if (btn && hasActiveSubscription) {
      // Hide button if user has active subscription
      btn.style.display = "none";
    } else if (btn && this.isAuthenticated()) {
      // Change button action if logged in
      btn.onclick = () => window.appNavigator.startSubscription();
    }

    // Initialize the pricing UI
    await this.initializePricingUI({
      toggleId: "billingToggle",
      priceId: "basicPrice",
      periodId: "basicPeriod",
      creditAmountId: "basicCreditAmount",
      monthlyLabelId: "monthlyLabel",
      yearlyLabelId: "yearlyLabel",
      countrySelectId: "countrySelect",
    });
  }

  updatePricingSubscriptionState({
    isLoggedIn,
    currentPricingPlan,
    hasActiveSubscription,
    currentSubscription,
  }) {
    const freeTrialCard = document.getElementById("freeTrialPricingCard");
    const basicCard = document.getElementById("basicPricingCard");
    const freeTrialButton = document.getElementById("freeTrialCreateAccountBtn");
    const basicBadge = document.getElementById("basicCurrentPlan");
    const freeTrialBadge = document.getElementById("freeTrialCurrentPlan");

    freeTrialButton?.classList.toggle("hidden", isLoggedIn);
    freeTrialCard?.classList.toggle(
      "pricing-card-current",
      isLoggedIn && !hasActiveSubscription && currentPricingPlan === "free-trial",
    );
    basicCard?.classList.toggle(
      "pricing-card-current",
      hasActiveSubscription && currentPricingPlan === "basic",
    );

    if (freeTrialBadge) {
      freeTrialBadge.textContent = "Current plan";
    }

    if (basicBadge) {
      const label = currentSubscription?.planName || "Basic";
      basicBadge.textContent = `Current plan: ${label}`;
    }
  }

  /**
   * Reusable pricing UI initialization
   */
  async initializePricingUI(elementIds) {
    const toggle = document.getElementById(elementIds.toggleId);
    const price = document.getElementById(elementIds.priceId);
    const period = document.getElementById(elementIds.periodId);
    const creditAmount = document.getElementById(elementIds.creditAmountId);
    const monthlyLabel = document.getElementById(elementIds.monthlyLabelId);
    const yearlyLabel = document.getElementById(elementIds.yearlyLabelId);
    const countrySelect = document.getElementById(elementIds.countrySelectId);
    const vatBadge = document.getElementById(`${elementIds.priceId}_vat`);

    if (
      !toggle ||
      !price ||
      !period ||
      !creditAmount ||
      !monthlyLabel ||
      !yearlyLabel ||
      !countrySelect
    ) {
      return;
    }

    const basicPricing = getPricingProducts().basic.pricing;
    const BASE_MONTHLY = basicPricing.monthly.base;
    const BASE_YEARLY = basicPricing.yearly.base;
    const BASE_MONTHLY_CREDIT = basicPricing.monthly.credit;
    const BASE_YEARLY_CREDIT = basicPricing.yearly.credit;

    // Store PPP data
    let pppData = {};
    let currentMultiplier = 1.0;

    // Load and parse PPP data
    try {
      // Valid ISO 3166-1 alpha-3 country codes (actual countries, not regions/aggregates)
      const validCountryCodes = new Set([
        "AFG",
        "ALB",
        "DZA",
        "ASM",
        "AND",
        "AGO",
        "AIA",
        "ATG",
        "ARG",
        "ARM",
        "ABW",
        "AUS",
        "AUT",
        "AZE",
        "BHS",
        "BHR",
        "BGD",
        "BRB",
        "BLR",
        "BEL",
        "BLZ",
        "BEN",
        "BMU",
        "BTN",
        "BOL",
        "BIH",
        "BWA",
        "BRA",
        "BRN",
        "BGR",
        "BFA",
        "BDI",
        "KHM",
        "CMR",
        "CAN",
        "CPV",
        "CYM",
        "CAF",
        "TCD",
        "CHL",
        "CHN",
        "COL",
        "COM",
        "COG",
        "COD",
        "CRI",
        "CIV",
        "HRV",
        "CUB",
        "CUW",
        "CYP",
        "CZE",
        "DNK",
        "DJI",
        "DMA",
        "DOM",
        "ECU",
        "EGY",
        "SLV",
        "GNQ",
        "ERI",
        "EST",
        "SWZ",
        "ETH",
        "FLK",
        "FRO",
        "FJI",
        "FIN",
        "FRA",
        "GUF",
        "PYF",
        "GAB",
        "GMB",
        "GEO",
        "DEU",
        "GHA",
        "GIB",
        "GRC",
        "GRL",
        "GRD",
        "GLP",
        "GUM",
        "GTM",
        "GGY",
        "GIN",
        "GNB",
        "GUY",
        "HTI",
        "HND",
        "HKG",
        "HUN",
        "ISL",
        "IND",
        "IDN",
        "IRN",
        "IRQ",
        "IRL",
        "IMN",
        "ISR",
        "ITA",
        "JAM",
        "JPN",
        "JEY",
        "JOR",
        "KAZ",
        "KEN",
        "KIR",
        "PRK",
        "KOR",
        "KWT",
        "KGZ",
        "LAO",
        "LVA",
        "LBN",
        "LSO",
        "LBR",
        "LBY",
        "LIE",
        "LTU",
        "LUX",
        "MAC",
        "MDG",
        "MWI",
        "MYS",
        "MDV",
        "MLI",
        "MLT",
        "MHL",
        "MTQ",
        "MRT",
        "MUS",
        "MYT",
        "MEX",
        "FSM",
        "MDA",
        "MCO",
        "MNG",
        "MNE",
        "MSR",
        "MAR",
        "MOZ",
        "MMR",
        "NAM",
        "NRU",
        "NPL",
        "NLD",
        "NCL",
        "NZL",
        "NIC",
        "NER",
        "NGA",
        "NIU",
        "NFK",
        "MKD",
        "MNP",
        "NOR",
        "OMN",
        "PAK",
        "PLW",
        "PSE",
        "PAN",
        "PNG",
        "PRY",
        "PER",
        "PHL",
        "PCN",
        "POL",
        "PRT",
        "PRI",
        "QAT",
        "REU",
        "ROU",
        "RUS",
        "RWA",
        "BLM",
        "SHN",
        "KNA",
        "LCA",
        "MAF",
        "SPM",
        "VCT",
        "WSM",
        "SMR",
        "STP",
        "SAU",
        "SEN",
        "SRB",
        "SYC",
        "SLE",
        "SGP",
        "SXM",
        "SVK",
        "SVN",
        "SLB",
        "SOM",
        "ZAF",
        "SGS",
        "SSD",
        "ESP",
        "LKA",
        "SDN",
        "SUR",
        "SJM",
        "SWE",
        "CHE",
        "SYR",
        "TWN",
        "TJK",
        "TZA",
        "THA",
        "TLS",
        "TGO",
        "TKL",
        "TON",
        "TTO",
        "TUN",
        "TUR",
        "TKM",
        "TCA",
        "TUV",
        "UGA",
        "UKR",
        "ARE",
        "GBR",
        "USA",
        "UMI",
        "URY",
        "UZB",
        "VUT",
        "VEN",
        "VNM",
        "VGB",
        "VIR",
        "WLF",
        "ESH",
        "YEM",
        "ZMB",
        "ZWE",
      ]);

      // Country name overrides
      const countryNameOverrides = {
        "West Bank and Gaza": "Palestine",
      };

      const response = await fetch("/data/ppp/ppp.csv");
      const csvText = await response.text();
      const lines = csvText.split("\n");

      // Parse CSV line properly
      const parseCSVLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
          const char = line[i];

          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === "," && !inQuotes) {
            result.push(current);
            current = "";
          } else {
            current += char;
          }
        }
        result.push(current);
        return result;
      };

      // Helper to find latest available GDP value
      const getLatestGdp = (parts) => {
        // Start from the end (most recent year) and work backwards
        for (let i = parts.length - 1; i >= 4; i--) {
          const value = parseFloat(parts[i]);
          if (value && !isNaN(value) && value > 0) {
            return value;
          }
        }
        return null;
      };

      // Find US GDP per capita
      const usLine = lines.find((line) => line.includes('"United States"'));
      if (!usLine) throw new Error("US data not found");

      const usParts = parseCSVLine(usLine);
      const usGdp = getLatestGdp(usParts);

      if (!usGdp || isNaN(usGdp)) {
        throw new Error("Invalid US GDP data");
      }

      console.log("US GDP per capita (latest):", usGdp);

      // Parse all countries
      const countries = [];
      for (const line of lines) {
        if (
          !line.includes("GDP per capita, PPP") ||
          line.startsWith('"Data Source')
        )
          continue;

        const parts = parseCSVLine(line);
        if (parts.length < 5) continue;

        let countryName = parts[0];
        const countryCode = parts[1];

        // Apply country name override if exists
        if (countryNameOverrides[countryName]) {
          countryName = countryNameOverrides[countryName];
        }

        // Skip if not a valid country code
        if (!validCountryCodes.has(countryCode)) {
          continue;
        }

        const gdp = getLatestGdp(parts);

        // Skip if no valid GDP data
        if (!gdp) {
          continue;
        }

        // Calculate PPP multiplier (ratio of country GDP to US GDP)
        const multiplier = gdp / usGdp;

        countries.push({
          name: countryName,
          code: countryCode,
          gdp: gdp,
          multiplier: multiplier,
        });

        pppData[countryCode] = multiplier;
      }

      console.log("Loaded countries:", countries.length);

      // Fetch detected country from IP
      let detectedCountryCode = "USA"; // Default fallback
      try {
        const countryResponse = await fetch("/api/subscription/countries");
        if (countryResponse.ok) {
          const countryData = await countryResponse.json();
          detectedCountryCode = countryData.detectedCountry || "USA";
          console.log("Detected country from IP:", detectedCountryCode);
        }
      } catch (error) {
        console.error("Failed to detect country:", error);
      }

      // Sort countries alphabetically
      countries.sort((a, b) => a.name.localeCompare(b.name));

      // Populate dropdown
      countrySelect.innerHTML = "";
      countries.forEach((country) => {
        const option = document.createElement("option");
        option.value = country.code;
        option.textContent = country.name;
        // Select detected country or USA as fallback
        if (country.code === detectedCountryCode) {
          option.selected = true;
          currentMultiplier = pppData[country.code] || 1.0;
        }
        countrySelect.appendChild(option);
      });
    } catch (error) {
      console.error("Error loading PPP data:", error);
      countrySelect.innerHTML =
        '<option value="USA">United States (default)</option>';
    }

    // Track current displayed values for animation
    let currentDisplayedPrice = null;
    let currentDisplayedCredit = null;
    let priceAnimationFrame = null;
    let creditAnimationFrame = null;

    // Animate a number from start to end over duration
    function animatePrice(element, startValue, endValue, duration, formatFn) {
      const steps = 10;
      const stepDuration = duration / steps;
      let currentStep = 0;

      function animate() {
        currentStep++;
        const progress = currentStep / steps;
        const currentValue = startValue + (endValue - startValue) * progress;
        element.innerHTML = formatFn(currentValue);

        if (currentStep < steps) {
          priceAnimationFrame = setTimeout(animate, stepDuration);
        }
      }

      if (priceAnimationFrame) {
        clearTimeout(priceAnimationFrame);
      }
      animate();
    }

    function animateCredit(element, startValue, endValue, duration, formatFn) {
      const steps = 10;
      const stepDuration = duration / steps;
      let currentStep = 0;

      function animate() {
        currentStep++;
        const progress = currentStep / steps;
        const currentValue = startValue + (endValue - startValue) * progress;
        element.innerHTML = formatFn(currentValue);

        if (currentStep < steps) {
          creditAnimationFrame = setTimeout(animate, stepDuration);
        }
      }

      if (creditAnimationFrame) {
        clearTimeout(creditAnimationFrame);
      }
      animate();
    }

    function updatePricing() {
      try {
        const isYearly = toggle.checked;
        const selectedCountry = countrySelect.value;

        // Apply interpolation between US price (1.0) and PPP-adjusted price (currentMultiplier)
        // Formula: finalMultiplier = 1.0 + (multiplier - 1.0) * interpolation
        const interpolatedMultiplier =
          1.0 + (currentMultiplier - 1.0) * settings.pppInterpolation;

        let monthly = BASE_MONTHLY * interpolatedMultiplier;
        let yearly = BASE_YEARLY * interpolatedMultiplier;
        // Credits are always 50% of base price (without VAT)
        let monthlyCredit = BASE_MONTHLY_CREDIT * interpolatedMultiplier;
        let yearlyCredit = BASE_YEARLY_CREDIT * interpolatedMultiplier;

        // Prices are shown excluding VAT; Stripe Tax adds VAT at checkout.
        const vatRate = EU_VAT_RATES[selectedCountry];
        const isEU = vatRate !== undefined;

        // Show/hide VAT badge with percentage
        if (vatBadge) {
          if (isEU) {
            const vatPercent = (vatRate * 100).toFixed(0);
            vatBadge.textContent = `excl. ${vatPercent}% VAT`;
            vatBadge.style.display = "inline";
          } else {
            vatBadge.style.display = "none";
          }
        }

        const targetPrice = isYearly ? yearly : monthly;
        const targetCredit = isYearly ? yearlyCredit : monthlyCredit;
        const periodText = isYearly ? "per year" : "per month";
        const creditText = isYearly
          ? " AI agent credit included per year"
          : " AI agent credit included per month";

        // Update period text immediately
        period.textContent = periodText;

        // Update toggle labels
        if (isYearly) {
          monthlyLabel.classList.remove("active");
          yearlyLabel.classList.add("active");
        } else {
          monthlyLabel.classList.add("active");
          yearlyLabel.classList.remove("active");
        }

        // Animate price if we have a previous value, otherwise set directly
        if (currentDisplayedPrice !== null) {
          animatePrice(
            price,
            currentDisplayedPrice,
            targetPrice,
            300,
            (val) => `<span class="pricing-currency">€</span>${val.toFixed(2)}`,
          );
        } else {
          price.innerHTML = `<span class="pricing-currency">€</span>${targetPrice.toFixed(
            2,
          )}`;
        }
        currentDisplayedPrice = targetPrice;

        // Animate credit amount
        if (currentDisplayedCredit !== null) {
          animateCredit(
            creditAmount,
            currentDisplayedCredit,
            targetCredit,
            300,
            (val) =>
              `<span class="pricing-currency">€</span>${val.toFixed(
                2,
              )}${creditText}`,
          );
        } else {
          creditAmount.innerHTML = `<span class="pricing-currency">€</span>${targetCredit.toFixed(
            2,
          )}${creditText}`;
        }
        currentDisplayedCredit = targetCredit;
      } catch (error) {
        console.error("Error in updatePricing:", error);
      }
    }

    // Handle country selection
    countrySelect.addEventListener("change", function () {
      const selectedCode = countrySelect.value;
      currentMultiplier = pppData[selectedCode] || 1.0;
      updatePricing();
    });

    // Set initial state
    monthlyLabel.classList.add("active");
    updatePricing();

    // Add event listeners for billing toggle
    toggle.addEventListener("change", updatePricing);

    // Make labels clickable
    monthlyLabel.addEventListener("click", function (e) {
      e.preventDefault();
      toggle.checked = false;
      updatePricing();
    });

    yearlyLabel.addEventListener("click", function (e) {
      e.preventDefault();
      toggle.checked = true;
      updatePricing();
    });
  }

  /**
   * Swap handbook screenshots between light/dark assets to match theme.
   */
  refreshDocumentationScreenshots() {
    const isLight =
      document.documentElement.getAttribute("data-theme") === "light";
    document
      .querySelectorAll("img.docs-screenshot[data-dark-src]")
      .forEach((img) => {
        const lightSrc = img.getAttribute("data-light-src");
        const darkSrc = img.getAttribute("data-dark-src");
        if (!lightSrc || !darkSrc) return;
        const nextSrc = isLight ? lightSrc : darkSrc;
        if (img.getAttribute("src") !== nextSrc) {
          img.setAttribute("src", nextSrc);
        }
      });
  }

  /**
   * Keep the active handbook Contents item visible in the sticky sidebar.
   */
  scrollDocumentationTocIntoView() {
    const current = document.querySelector(".article-toc a.is-current");
    if (current && typeof current.scrollIntoView === "function") {
      current.scrollIntoView({ block: "nearest" });
    }
  }

  /**
   * Initialize article TOC with visibility-based opacity
   */
  initializeArticleTOC() {
    // Clean up previous TOC listeners if they exist
    if (this._articleTOCCleanup) {
      this._articleTOCCleanup();
      this._articleTOCCleanup = null;
    }

    const tocLinks = document.querySelectorAll(".article-toc a");
    if (tocLinks.length === 0) return;

    // Create a map of section ranges to their corresponding TOC links
    const sectionRanges = [];
    tocLinks.forEach((link) => {
      const href = link.getAttribute("href");
      if (href && href.startsWith("#")) {
        const sectionId = href.substring(1);
        const heading = document.getElementById(sectionId);
        if (heading) {
          sectionRanges.push({ heading, link });
        }
      }
    });

    if (sectionRanges.length === 0) return;

    // Calculate section boundaries (from heading to next heading)
    sectionRanges.forEach((range, index) => {
      const nextHeading = sectionRanges[index + 1]?.heading;
      range.endElement = nextHeading || null;
    });

    // Function to calculate visibility of a section range
    const calculateSectionVisibility = (startElement, endElement) => {
      const windowHeight = window.innerHeight;
      const topDeadZone = 40;
      const bottomDeadZone = 60;
      const effectiveViewportTop = topDeadZone;
      const effectiveViewportBottom = windowHeight - bottomDeadZone;

      // Get the top of the section
      const sectionTop = startElement.getBoundingClientRect().top;

      // Get the bottom of the section (either the next heading or end of article)
      let sectionBottom;
      if (endElement) {
        sectionBottom = endElement.getBoundingClientRect().top;
      } else {
        // Last section - use the article container's bottom
        const articleDetail = document.querySelector(".article-detail");
        sectionBottom = articleDetail
          ? articleDetail.getBoundingClientRect().bottom
          : sectionTop + 1000;
      }

      // Calculate total section height
      const totalHeight = sectionBottom - sectionTop;
      if (totalHeight <= 0) return 0;

      // If section is completely above effective viewport
      if (sectionBottom < effectiveViewportTop) return 0;

      // If section is completely below effective viewport
      if (sectionTop > effectiveViewportBottom) return 0;

      // Calculate what portion of the section is visible within the effective viewport
      const viewportSectionTop = Math.max(effectiveViewportTop, sectionTop);
      const viewportSectionBottom = Math.min(
        effectiveViewportBottom,
        sectionBottom,
      );
      const visibleHeight = Math.max(
        0,
        viewportSectionBottom - viewportSectionTop,
      );

      // Use the smaller of section height or viewport height as normalizer
      // Short sections: normalized by their own height (full opacity when fully visible)
      // Long sections: normalized by viewport height (full opacity when filling viewport)
      const effectiveViewportHeight =
        effectiveViewportBottom - effectiveViewportTop;
      const normalizer = Math.min(totalHeight, effectiveViewportHeight);
      return Math.max(0, Math.min(1, visibleHeight / normalizer));
    };

    // Function to update TOC line opacities
    const updateTOCOpacity = () => {
      sectionRanges.forEach((range) => {
        const visibility = calculateSectionVisibility(
          range.heading,
          range.endElement,
        );
        // Apply power curve to make fade-out more pronounced (earlier and steeper)
        // Using power of 2.5 makes the opacity drop off more quickly as content scrolls away
        const opacity = Math.pow(visibility, 2.5);
        range.link.style.setProperty(
          "--visibility-opacity",
          opacity.toFixed(3),
        );
      });
    };

    // Initial update
    updateTOCOpacity();

    // Update on scroll with throttling for performance
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        window.cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = window.requestAnimationFrame(updateTOCOpacity);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Also update on window resize
    let resizeTimeout;
    const handleResize = () => {
      if (resizeTimeout) {
        window.cancelAnimationFrame(resizeTimeout);
      }
      resizeTimeout = window.requestAnimationFrame(updateTOCOpacity);
    };

    window.addEventListener("resize", handleResize, { passive: true });

    // Clean up listeners when navigating away
    const cleanup = () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };

    // Store cleanup function for potential future use
    this._articleTOCCleanup = cleanup;
  }

  /**
   * Show success message
   */
  showAuthSuccess(message) {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 60px;
      right: 20px;
      background: rgba(46, 213, 115, 0.95);
      color: white;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      z-index: 10000;
      font-family: var(--font-families-sans);
      font-size: 14px;
      max-width: 400px;
    `;
    notification.textContent = `✓ ${message}`;

    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transition = "opacity 0.3s ease";
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }
}

// Initialize the app navigator
const app = new AppNavigator();

// Expose to window for potential external control
window.appNavigator = app;
